import { createElementFromHTML, decodeHTMLEntities, arraysEqual, getLazyLoadComponentObserver, parseConfig } from '@utilities/theme';
import { normalizeVariantId } from '@utilities/product';

if (!window.customElements.get("cyo-product")) {
  /**
   * Availability Manager - Centralized availability checking logic
   */
  class CYOAvailabilityManager {
    constructor(debugMode = false) {
      this.debugMode = debugMode;
    }

    /**
     * Check if a product is available based on all availability rules
     * @param {Object} product - The product object to check
     * @returns {boolean} True if product is available
     */
    isProductAvailable(product) {
      try {
        if (product.available_shopify === false) {
          if (this.debugMode) {
            console.log(
              `🚫 CYO: Product ${product.id} (${product.title}) filtered out - available_shopify: false`
            );
          }
          return false;
        }

        if (product.available_greyson === false) {
          if (this.debugMode) {
            console.log(
              `🚫 CYO: Product ${product.id} (${product.title}) filtered out - available_greyson: false`
            );
          }
          return false;
        }

        return true;
      } catch (error) {
        console.error("❌ CYO: Error checking product availability:", error);
        return false;
      }
    }

    /**
     * Check if a variant is available based on all availability rules
     * @param {Object} variant - The variant object to check
     * @returns {boolean} True if variant is available
     */
    isVariantAvailable(variant) {
      try {
        if (variant.available_shopify === false) {
          if (this.debugMode) {
            console.log(
              `🚫 CYO: Variant ${variant.id} filtered out - available_shopify: false`
            );
          }
          return false;
        }

        if (variant.available_greyson === false) {
          if (this.debugMode) {
            console.log(
              `🚫 CYO: Variant ${variant.id} filtered out - available_greyson: false`
            );
          }
          return false;
        }

        return true;
      } catch (error) {
        console.error("❌ CYO: Error checking variant availability:", error);
        return false;
      }
    }

    /**
     * Get available variants for a product based on allowed colors/sizes
     * @param {Object} product - The product object
     * @param {Object} option - The option with restrictions
     * @returns {Array} Array of available variants
     */
    getAvailableVariants(product, option) {
      try {
        // Normalize allowed colors/sizes to uppercase for case-insensitive comparison
        const normalizedAllowedColors = option.allowed_colors?.length > 0
          ? option.allowed_colors.map(c => c.toUpperCase())
          : [];
        const normalizedAllowedSizes = option.allowed_sizes?.length > 0
          ? option.allowed_sizes.map(s => s.toUpperCase())
          : [];

        return product.variants.filter((variant) => {
          if (!this.isVariantAvailable(variant)) return false;

          if (normalizedAllowedColors.length > 0 && product.has_color_option) {
            if (!normalizedAllowedColors.includes(variant.color?.toUpperCase())) return false;
          }

          if (normalizedAllowedSizes.length > 0 && product.has_size_option) {
            const sizeOption = variant.options[product.size_option_index];
            if (!normalizedAllowedSizes.includes(sizeOption?.toUpperCase())) return false;
          }

          return true;
        });
      } catch (error) {
        console.error("❌ CYO: Error getting available variants:", error);
        return [];
      }
    }
  }

  /**
   * Accessibility Manager - Handles all accessibility features
   */
  class CYOAccessibilityManager {
    constructor(shadowRoot, debugMode = false) {
      this.shadowRoot = shadowRoot;
      this.debugMode = debugMode;
      this.liveRegion = null;
      this.lastFocusedElement = null;
      this.keyboardNavigation = false;
      this.setupAccessibility();
    }

    setupAccessibility() {
      this.liveRegion = document.createElement("div");
      this.liveRegion.className = "cyo-live-region";
      this.liveRegion.setAttribute("aria-live", "polite");
      this.liveRegion.setAttribute("aria-atomic", "true");
      this.liveRegion.id = "cyo-live-region";
      this.shadowRoot.appendChild(this.liveRegion);

      const progress = document.createElement("div");
      progress.className = "cyo-progress";
      progress.setAttribute("aria-live", "polite");
      progress.id = "cyo-progress";
      this.shadowRoot.appendChild(progress);
    }

    announce(message, priority = "polite") {
      if (!this.liveRegion) return;
      this.liveRegion.setAttribute("aria-live", priority);
      this.liveRegion.textContent = message;
      setTimeout(() => {
        if (this.liveRegion) this.liveRegion.textContent = "";
      }, 1000);
    }

    updateProgress(currentStep, totalSteps, stepName) {
      const progressElement = this.shadowRoot.querySelector("#cyo-progress");
      if (progressElement) {
        progressElement.textContent = `Step ${currentStep} of ${totalSteps}: ${stepName}`;
      }
    }

    handleKeyboardNavigation(event) {
      this.keyboardNavigation = true;
      document.documentElement.setAttribute("data-keyboard-navigation", "true");

      const { key, target } = event;

      if (key === "Escape") return "escape";

      // A native button already fires click on Enter.
      if (key === "Enter" && target.tagName === "BUTTON") return "enter";

      if (["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(key)) {
        return this.handleArrowKeyNavigation(event);
      }

      return null;
    }

    handleArrowKeyNavigation(event) {
      const { key, target } = event;
      if (target.type !== "radio") return null;

      const radioGroup = target.closest("fieldset");
      if (!radioGroup) return null;

      const radios = Array.from(
        radioGroup.querySelectorAll('input[type="radio"]')
      );
      const currentIndex = radios.indexOf(target);
      if (currentIndex === -1) return null;

      let nextIndex;
      if (key === "ArrowDown" || key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % radios.length;
      } else if (key === "ArrowUp" || key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + radios.length) % radios.length;
      } else {
        return null;
      }

      event.preventDefault();
      const nextRadio = radios[nextIndex];
      nextRadio.focus();
      nextRadio.checked = true;
      nextRadio.dispatchEvent(new Event("change", { bubbles: true }));
      return "arrow";
    }
  }

  /**
   * Debounce Manager - Handles all debouncing operations
   */
  class CYODebounceManager {
    constructor() {
      this.timers = new Map();
    }

    debounce(key, callback, delay) {
      if (this.timers.has(key)) {
        clearTimeout(this.timers.get(key));
      }

      const timer = setTimeout(() => {
        callback();
        this.timers.delete(key);
      }, delay);

      this.timers.set(key, timer);
    }

    clearAll() {
      this.timers.forEach((timer) => clearTimeout(timer));
      this.timers.clear();
    }

    clear(key) {
      if (this.timers.has(key)) {
        clearTimeout(this.timers.get(key));
        this.timers.delete(key);
      }
    }
  }

  /**
   * Create Your Own Product Component
   *
   * A web component that implements step-by-step product customization with linear
   * navigation and state preservation. Handles product selection, variant selection
   * (color/size/other options), real-time pricing updates, and seamless navigation
   * between steps with full accessibility support.
   *
   * ## Shadow DOM Implementation
   * - Uses shadow DOM for complete style isolation
   * - Clones CSS template content directly for optimal performance
   * - Automatic swatch color support via critical CSS inclusion
   * - Isolated event handling with delegation from shadow root
   *
   * ## Accessibility Features
   * - Uses semantic radio inputs for single selection options
   * - Proper form labels, fieldsets, and legends
   * - ARIA attributes for screen reader support
   * - Keyboard navigation support
   * - Focus management for modal-style interactions
   * - Accessible color swatches with hidden text labels
   *
   * ## Navigation Flow
   * - Linear progression: Step 1 → Step 2 → ... → Review → Add to Bag
   * - Forward navigation requires complete selection (product + variant)
   * - Backward navigation preserves all existing selections
   * - State restoration when returning to previous steps
   * - Review step displays when currentStepIndex >= total steps
   * - Dual navigation: header (desktop) and footer (mobile) action buttons
   *
   * ## State Management
   * - currentStepIndex: Main step tracker (0-based: 0=Step1, 1=Step2, etc.)
   * - currentPath: Reserved for linked step navigation (not fully implemented)
   * - selections: Map<stepId, {step, option, product, variant}> for complete selections
   * - Smart variant matching based on previous color/size selections
   * - Auto-selection support with configurable first-option behavior
   *
   * ## Pricing Integration
   * - Two pricing modes: static bundle pricing and aggregate pricing
   * - Static mode: Uses pre-calculated bundle_product.price and skips all calculations/API calls
   * - Aggregate mode: Real-time price calculation from all selected variants
   * - Server-side price formatting via AJAX requests to bundle product (aggregate only)
   * - Debounced price updates (300ms) to prevent API spam (aggregate only)
   * - Integration with parent product-overview component
   * - Support for both regular and compare-at pricing
   *
   * ## Dynamic Option Discovery
   * - Automatic detection of product option types (color, size, custom)
   * - Cached option type metadata for performance
   * - Flexible rendering: color swatches vs. button selections
   * - Support for products with varying option structures
   * - Configurable single-option visibility per step
   *
   * ## Interaction Handling
   * - Click debouncing (150ms) to prevent rapid successive interactions
   * - Event delegation from shadow root for optimal performance
   * - Support for color swatch, size button, and product option clicks
   * - Automatic radio input triggering for visual selections
   *
   * ## Configuration
   * - JSON configuration via script tag with type="application/json"
   * - Configurable auto-selection: select_first_available_option
   * - Step-level allowed_option_selectors for general option visibility control
   * - Step-level allowed_single_option_selectors for single-option UI control
   * - Product-level color/size restrictions per option
   * - Bundle product integration for pricing and cart functionality
   * - Product options pagination control via "pagination" object:
   *   - pagination.enabled: Enable/disable pagination (default: false)
   *   - pagination.options_per_page: Threshold and page size for pagination (default: 8)
   *
   * ## Example Usage
   * ```html
   * <cyo-product class="debug">
   *   <template component-styles>
   *     <style></style>
   *     <link rel="stylesheet" href="component.css" />
   *   </template>
   *   <script type="application/json">
   *     {
   *       "select_first_available_option": true,
   *       "pagination": {
   *         "enabled": true,
   *         "options_per_page": 8
   *       },
   *       "steps": [
   *         {
   *           "id": "step1",
   *           "label": "Choose Your Base",
   *           "name": "Base Style",
   *           "allowed_option_selectors": ["Color", "Size"],
   *           "allowed_single_option_selectors": ["Color"],
   *           "options": []
   *         }
   *       ],
   *       "bundle_product": {
   *         "url": "/products/bundle-product",
   *         "id": 123
   *       }
   *     }
   *   </script>
   * </cyo-product>
   * ```
   *
   * ## Events Dispatched
   * - `cyo:price-update`: When total price changes
   * - `cyo:add-to-bag`: When user completes selection and adds to cart
   *
   * ## Debug Mode
   * Add `class="debug"` to enable console logging for development.
   *
   * @class CreateYourOwnProduct
   * @extends HTMLElement
   * @version 2.0.0
   */
  class CYOProduct extends HTMLElement {
    /**
     * Observed attributes for real-time updates
     * @returns {Array<string>} Array of attribute names to observe
     */
    static get observedAttributes() {
      return ["data-cart-transform", "data-enable-sticky-atb", "data-enable-sticky-atb-on"];
    }

    /**
     * Cache for product option types to avoid repeated computation
     * @type {Map<number, Array>} Map of product ID to option types
     */
    #productOptionTypesCache = new Map();

    /**
     * Initialize the component with default navigation and pricing state
     *
     * Sets up core navigation tracking, selection storage, price calculation
     * state, and shadow DOM. All navigation starts at step 0 with empty selections.
     */
    constructor() {
      super();

      // Create shadow DOM for style isolation
      this.attachShadow({ mode: "open" });

      // Configuration and Setup
      /** @type {Object} Configuration data parsed from JSON script tag */
      this.config = {};
      /** @type {boolean} Auto-select first available option when rendering steps */
      this.selectFirstAvailableOption = false;
      /** @type {string} Cart transformation mode: 'merge' or 'expand' */
      this.cartTransformation = "merge";

      // Navigation State - Core tracking for step progression
      /** @type {number} Current main step index (0=Step1, 1=Step2, 2=Review for 2-step flow) */
      this.currentStepIndex = 0;
      /** @type {Array<number>} Navigation path through linked sub-steps (NOT FULLY IMPLEMENTED) */
      this.currentPath = [];

      // Selection State - Preserves user choices across navigation
      /**
       * @type {Map<string, Object>} Complete selections keyed by step ID
       * Each selection contains: {step, option, product, variant}
       */
      this.selections = new Map();

      // Integration and Pricing
      /** @type {HTMLElement|null} Parent product-overview component for price updates */
      this.productOverview = null;
      /** @type {number} Current price from selections or bundle product (in cents) */
      this.price = 0;
      /** @type {number} Current compare-at price from selections or bundle product (in cents) */
      this.compareAtPrice = 0;
      /** @type {string|null} Formatted price HTML ready for display */
      this.priceHTML = null;
      /** @type {number} Debounce delay for click interactions in milliseconds */
      this.clickDebounceDelay = 150;
      /** @type {number} Debounce delay for media updates in milliseconds */
      this.mediaUpdateDelay = 300;
      /** @type {number} Debounce delay for hover/focus media prefetch */
      this.aggregatePrefetchDebounceDelay = 50;
      /** @type {Set<string>} Track prefetched aggregate URLs to avoid duplicate requests */
      this.prefetchedAggregateMediaUrls = new Set();

      // Sub-component managers (initialized in setupShadowDOM)
      /** @type {CYOAvailabilityManager} Handles availability checking */
      this.availabilityManager = null;
      /** @type {CYOAccessibilityManager} Handles accessibility features */
      this.accessibilityManager = null;
      /** @type {CYODebounceManager} Handles all debouncing operations */
      this.debounceManager = new CYODebounceManager();

      // Media Update State Management
      /** @type {string|null} Current step 1 media URL for fallback when no aggregate exists */
      this.currentStep1MediaUrl = null;
      /** @type {string|null} Currently displayed media URL to prevent redundant updates */
      this.currentDisplayedMediaUrl = null;
      /** @type {Object|null} Current bundle featured media info for cart transformation */
      this.currentBundleFeaturedMedia = null;

      // Product Options Pagination
      /** @type {Object} Zone-specific expansion state - tracks which zones are expanded */
      this.zoneExpansionState = {};
      /** @type {Object} Default product options pagination configuration (fallback) */
      this.defaultProductOptionsPagination = {
        enabled: false,
        optionsPerPage: 8,
        viewMoreCta: "MORE OPTIONS +",
        viewLessCta: "LESS OPTIONS -",
      };
      // Sticky Add to Bag Functionality
      /** @type {boolean} Whether sticky add to bag is enabled */
      this.enableStickyATB = false;
      /** @type {string} When to show sticky add to bag: 'render' or 'scroll' */
      this.enableStickyATBOn = 'scroll';
      /** @type {Function} Bound reference for scroll handler */
      this.boundHandleStickyATC = null;
      /** @type {Function} Bound reference for resize handler */
      this.boundHandleResize = null;
      /** @type {number} Margin offset for sticky button positioning */
      this.atbButtonMarginOffset = 0;
    }

    /**
     * Setup shadow DOM with CSS template and content container
     * Enhanced with accessibility features
     */
    #setupShadowDOM() {
      // Find the CSS template
      const stylesTemplate = this.querySelector("template[component-styles]");
      if (stylesTemplate) {
        // Clone the template content (which includes <style> and <link> tags)
        const stylesContent = stylesTemplate.content.cloneNode(true);
        this.shadowRoot.appendChild(stylesContent);
      }

      // Initialize sub-component managers
      this.availabilityManager = new CYOAvailabilityManager(this.allowDebug());
      this.accessibilityManager = new CYOAccessibilityManager(
        this.shadowRoot,
        this.allowDebug()
      );

      // Add the content container
      const container = document.createElement("div");
      container.className = "cyo-container";
      container.id = "cyo-content";
      container.setAttribute("role", "main");
      container.setAttribute("aria-label", "Product customization interface");
      this.shadowRoot.appendChild(container);
    }

    /**
     * Determines if lazy loading is enabled for this product card
     * @returns {boolean} Whether lazy loading is enabled
     */
    isLazyLoad() {
      // return true if the component has the lazy-load class
      return this.classList.contains("lazy-load");
    }

    /**
     * Announce message to screen readers via live region
     * @param {string} message - Message to announce
     * @param {string} priority - 'polite' or 'assertive'
     */
    announceToScreenReader = (message, priority = "polite") => {
      this.accessibilityManager?.announce(message, priority);
    };

    /**
     * Update progress for screen readers
     * @param {number} currentStep - Current step number (1-based)
     * @param {number} totalSteps - Total number of steps
     * @param {string} stepName - Name of current step
     */
    updateProgress = (currentStep, totalSteps, stepName) => {
      this.accessibilityManager?.updateProgress(
        currentStep,
        totalSteps,
        stepName
      );
    };

    // Accessibility methods moved to CYOAccessibilityManager

    /**
     * Handle keyboard navigation
     * @param {KeyboardEvent} event - Keyboard event
     */
    handleKeyboardNavigation = (event) => {
      const result = this.accessibilityManager?.handleKeyboardNavigation(event);

      if (result === "escape") {
        this.handleEscapeKey(event);
      }
      // Other keyboard actions are handled by the accessibility manager
    };

    /**
     * Handle escape key press
     * @param {KeyboardEvent} event - Keyboard event
     */
    handleEscapeKey = (event) => {
      // If we're not on the first step, go back
      if (this.currentStepIndex > 0) {
        event.preventDefault();
        this.handleBack();
      }
    };

    /**
     * Check if debug mode is enabled
     * @returns {boolean} True if debug class is present
     */
    allowDebug = () => {
      return this.classList.contains("debug");
    };

    /**
     * Check if a product is available based on all availability rules
     * @param {Object} product - The product object to check
     * @returns {boolean} True if product is available
     */
    isProductAvailable = (product) => {
      return this.availabilityManager?.isProductAvailable(product) ?? false;
    };

    /**
     * Check if a variant is available based on all availability rules
     * @param {Object} variant - The variant object to check
     * @returns {boolean} True if variant is available
     */
    isVariantAvailable = (variant) => {
      return this.availabilityManager?.isVariantAvailable(variant) ?? false;
    };

    /**
     * Component connected to DOM
     */
    async connectedCallback() {
      try {
        if (this.allowDebug()) {
          console.log("🔌 CYO: Component connected to DOM");
        }

        // Bind sticky ATB event handlers
        this.boundHandleStickyATC = this.handleStickyATC.bind(this);
        this.boundHandleResize = this.handleResize.bind(this);

        // Initialize sticky ATB state from attributes
        this.handleStickyATBChange();

        // lazy load flag
        if (this.isLazyLoad()) {
          // observe the component for lazy loading
          getLazyLoadComponentObserver().observe(this);
        } else {
          // initialize the component
          await this.init();
        }
      } catch (error) {
        console.error("❌ CYO: Error in connectedCallback:", error);
      }
    }

    /**
     * Handle attribute changes in real-time
     * @param {string} name - The attribute name that changed
     * @param {string|null} oldValue - The previous attribute value
     * @param {string|null} newValue - The new attribute value
     */
    attributeChangedCallback(name, oldValue, newValue) {
      try {
        if (name === "data-cart-transform") {
          const previousValue = this.cartTransformation;

          // Update cart transformation with the same logic as init
          if (newValue && ["merge", "expand"].includes(newValue)) {
            this.cartTransformation = newValue;
          } else {
            this.cartTransformation =
              this.config.cart_transformation || "merge";
          }

          // Only log if the value actually changed and debug is enabled
          if (this.allowDebug() && previousValue !== this.cartTransformation) {
            console.log(
              `⚙️ CYO: Cart transformation changed from "${previousValue}" to "${this.cartTransformation}" via data-cart-transform attribute`
            );
          }
        } else if (name === "data-enable-sticky-atb" || name === "data-enable-sticky-atb-on") {
          this.handleStickyATBChange();
        }
      } catch (error) {
        console.error("❌ CYO: Error in attributeChangedCallback:", error);
      }
    }

    /**
     * Component disconnected from DOM
     */
    disconnectedCallback() {
      try {
        if (this.allowDebug())
          console.log("🔌 CYO: Component disconnected from DOM");

        // Clear all debounced operations
        this.debounceManager.clearAll();

        // Reset loading state
        this.setAddingToBagState(false);

        // Disable sticky ATB events
        this.disableStickyATBEvents();

        // Remove event delegation listeners
        if (this.shadowRoot) {
          this.shadowRoot.removeEventListener(
            "change",
            this.handleDelegatedChange
          );
          this.shadowRoot.removeEventListener(
            "click",
            this.handleDelegatedClick
          );
          this.shadowRoot.removeEventListener(
            "keydown",
            this.handleKeyboardNavigation
          );
          this.shadowRoot.removeEventListener("focusin", this.handleFocusIn);
          this.shadowRoot.removeEventListener("mousedown", this.handleMouseDown);
          this.shadowRoot.removeEventListener("pointerover", this.handlePointerOver);
        }
      } catch (error) {
        console.error("❌ CYO: Error in disconnectedCallback:", error);
      }
    }

    /**
     * Initialize the component by parsing config and rendering
     */
    init = async () => {
      try {
        if (this.allowDebug()) console.log("🚀 CYO: Initializing component");

        // Setup shadow DOM once during initialization
        this.#setupShadowDOM();

        // Find parent product-overview component
        this.productOverview = this.closest("product-overview");
        if (this.productOverview) {
          if (this.allowDebug())
            console.log("🔗 CYO: Found parent product-overview component");
        } else {
          if (this.allowDebug())
            console.warn("⚠️ CYO: No parent product-overview component found");
        }

        this.config = parseConfig(this, this.allowDebug());
        if (this.allowDebug())
          console.log("📋 CYO: Parsed config:", this.config);

        if (this.config && this.config.steps) {
          if (this.allowDebug())
            console.log(
              `📊 CYO: Found ${this.config.steps.length} steps to render`
            );

          // Check for auto-selection configuration
          this.selectFirstAvailableOption =
            this.config.select_first_available_option ?? false;

          // Check for cart transformation configuration
          // First check for data attribute override, then fall back to config
          const dataCartTransform = this.getAttribute("data-cart-transform");
          if (
            dataCartTransform &&
            ["merge", "expand"].includes(dataCartTransform)
          ) {
            this.cartTransformation = dataCartTransform;
          } else {
            this.cartTransformation =
              this.config.cart_transformation || "merge";
          }

          // Configure default product options pagination (fallback for steps without pagination config)
          this.defaultProductOptionsPagination = {
            enabled: this.config.pagination?.enabled ?? false,
            optionsPerPage: this.config.pagination?.options_per_page ?? 8,
            viewMoreCta: this.config.pagination?.view_more_cta ?? "MORE OPTIONS +",
            viewLessCta: this.config.pagination?.view_less_cta ?? "LESS OPTIONS -",
          };

          if (this.allowDebug()) {
            console.log(
              `⚙️ CYO: Select first available option: ${this.selectFirstAvailableOption}`
            );
            console.log(
              `⚙️ CYO: Cart transformation mode: ${this.cartTransformation}`
            );
            console.log(
              `⚙️ CYO: Default pagination enabled: ${this.defaultProductOptionsPagination.enabled}`
            );
            console.log(
              `⚙️ CYO: Default options per page: ${this.defaultProductOptionsPagination.optionsPerPage}`
            );
          }

          await this.render();

          // Initialize pricing system
          await this.calculatePricing();

          // Initialize price display
          if (this.allowDebug()) {
            console.log("💰 CYO: Initializing pricing system");
          }
          await this.updatePrice();
        } else {
          console.warn("⚠️ CYO: No valid config or steps found");
          this.renderError("Failed to initialize component");
        }
      } catch (error) {
        console.error("❌ CYO: Error in init:", error);
        this.renderError("Failed to initialize component");
      }
      this.classList.add("loaded");
    };

    /**
     * Get current step based on navigation path and step index
     * Handles both main steps and linked steps (linked steps not fully implemented)
     * @returns {Object|null} Current step object or null if not found
     */
    getCurrentStep = () => {
      try {
        const step = this.config.steps[this.currentStepIndex];
        return step;
      } catch (error) {
        console.error("❌ CYO: Error getting current step:", error);
        return null;
      }
    };

    /**
     * Determine if the review step should be displayed
     */
    isReviewStep = () => {
      return this.currentStepIndex >= this.config.steps.length;
    };

    /**
     * Get selection for a specific step
     */
    getSelectionForStep = (step) => {
      if (!step) return null;
      return this.selections.get(step.id);
    };

    /**
     * Set selection for a specific step
     */
    setSelectionForStep = (step, selection, updatePrice = false) => {
      if (!step) return;

      this.selections.set(step.id, selection);

      if (updatePrice) {
        // Only update price if not using bundle product price
        if (!this.config.use_bundle_product_price) {
          this.debouncedUpdatePrice();
        } else if (this.allowDebug()) {
          console.log(
            "💰 CYO: Skipping price update - using bundle product price"
          );
        }
      }

      if (this.allowDebug()) {
        console.log(
          `💾 CYO: Selection saved for step "${step.label}":`,
          selection
        );
      }
    };

    /**
     * Universal pricing calculation method
     * Calculates and stores all pricing data in clean component properties
     * Handles both bundle product pricing and aggregate pricing modes
     */
    calculatePricing = async () => {
      let price = 0;
      let compareAtPrice = 0;

      // Check if we should use bundle product price instead of aggregate pricing
      if (this.config.use_bundle_product_price) {
        // Use the bundle product's static price
        price = this.config.bundle_product?.price || 0;
        compareAtPrice = this.config.bundle_product?.compare_at_price || 0;

        if (this.allowDebug()) {
          console.log(
            `💰 CYO: Using bundle product price - Price: ${price}, Compare: ${compareAtPrice}`
          );
        }
      } else {
        // Calculate aggregate price from all selections
        this.selections.forEach((selection) => {
          if (selection.variant) {
            price += selection.variant.price || 0;
            compareAtPrice += selection.variant.compare_at_price || 0;
          }
        });

        if (this.allowDebug()) {
          console.log(
            `💰 CYO: Calculated aggregate price - Price: ${price}, Compare: ${compareAtPrice}`
          );
        }
      }

      // Store the calculated values in clean component properties
      this.price = price;
      this.compareAtPrice = compareAtPrice;

      // Fetch and store formatted price HTML
      this.priceHTML = await this.fetchFormattedPrice();

      if (this.allowDebug()) {
        console.log(
          `💰 CYO: Pricing calculation complete - Price: ${
            this.price
          }, Compare: ${this.compareAtPrice}, HTML: ${!!this.priceHTML}`
        );
      }

      // Return the values for convenience
      return {
        price: this.price,
        compareAtPrice: this.compareAtPrice,
        priceHTML: this.priceHTML,
      };
    };

    /**
     * Debounced price update with recalculation
     */
    debouncedUpdatePrice = (delay = 300) => {
      this.debounceManager.debounce(
        "priceUpdate",
        async () => {
          // Recalculate pricing and then update display
          await this.calculatePricing();
          await this.updatePrice();
        },
        delay
      );
    };

    /**
     * Fetch formatted price from server using AJAX
     * @returns {Promise<string|null>} Formatted price HTML or null if failed
     */
    fetchFormattedPrice = async () => {
      try {
        // If using bundle product price, return the cached price HTML directly
        if (this.config.use_bundle_product_price) {
          const cachedPriceHTML = this.config.bundle_product?.price_html;

          if (cachedPriceHTML) {
            if (this.allowDebug()) {
              console.log("💰 CYO: Using cached bundle product price HTML");
            }
            return cachedPriceHTML;
          } else {
            if (this.allowDebug()) {
              console.warn(
                "⚠️ CYO: No cached bundle product price HTML available"
              );
            }
            return null;
          }
        }

        // Use aggregate pricing (existing behavior) - requires API call
        if (!this.config.bundle_product?.url) {
          if (this.allowDebug())
            console.warn("⚠️ CYO: No bundle product URL available");
          return null;
        }

        const url = `${this.config.bundle_product.url}?view=product-price&price=${this.price}&compareAtPrice=${this.compareAtPrice}`;

        if (this.allowDebug())
          console.log(`🌐 CYO: Fetching aggregate price from: ${url}`);

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        if (data.price_html) {
          if (this.allowDebug())
            console.log("💰 CYO: Received aggregate price from server");
          return data.price_html;
        } else {
          console.warn("⚠️ CYO: Server response missing price_html");
          return null;
        }
      } catch (error) {
        console.error("❌ CYO: Error fetching formatted price:", error);
        return null;
      }
    };

    /**
     * Update the bundle product price display via parent product-overview component
     * Uses stored pricing data from calculatePricing()
     */
    updatePrice = async () => {
      try {
        if (this.allowDebug()) console.log("💰 CYO: Starting price update");

        // Ensure we have pricing data
        if (this.price === undefined || this.price === null) {
          if (this.allowDebug()) {
            console.warn("⚠️ CYO: No pricing data available, calculating now");
          }
          // If no pricing data, calculate first and continue with display
          await this.calculatePricing();

          // If pricing calculation failed, log error and return
          if (this.price === undefined || this.price === null) {
            console.error("❌ CYO: Failed to calculate pricing data");
            return;
          }
        }

        // Use stored formatted price HTML
        if (
          this.priceHTML &&
          this.productOverview &&
          typeof this.productOverview.setPrice === "function"
        ) {
          this.productOverview.setPrice(this.priceHTML);
          if (this.allowDebug())
            console.log(
              "💰 CYO: Updated price via product-overview with stored HTML"
            );
        } else if (!this.priceHTML) {
          if (this.allowDebug())
            console.warn("⚠️ CYO: No formatted price HTML available");
        } else {
          if (this.allowDebug())
            console.warn(
              "⚠️ CYO: Product-overview component not available or setPrice method not found"
            );
        }
      } catch (error) {
        console.error("❌ CYO: Error updating price display:", error);
      }
    };

    /**
     * Get available variants for a product based on allowed colors/sizes
     */
    getAvailableVariants = (product, option) => {
      return (
        this.availabilityManager?.getAvailableVariants(product, option) ?? []
      );
    };

    /**
     * Get customer membership data from window.theme.customer.membership
     * @returns {Object|null} Customer membership data or null if not available
     */
    getCustomerMembership = () => {
      try {
        return window.theme?.customer?.membership || null;
      } catch (error) {
        console.error("❌ CYO: Error getting customer membership:", error);
        return null;
      }
    };

    /**
     * Validate if customer meets membership requirements for a given membership requirement
     * @param {Object} membershipRequirement - The membership requirement object
     * @returns {boolean} True if customer meets the requirement
     */
    validateMembershipRequirement = (membershipRequirement) => {
      try {
        // If no membership is required, always allow access
        if (!membershipRequirement?.loyalty_required) {
          return true;
        }

        const customerMembership = this.getCustomerMembership();
        const requiredRank = membershipRequirement?.loyalty_tier_required?.rank || 0;
        const customerRank = customerMembership?.level?.rank || 0;

        // Access is based on rank comparison only.
        return customerRank >= requiredRank;
      } catch (error) {
        console.error("❌ CYO: Error validating membership requirement:", error);
        return false;
      }
    };

    /**
     * Check if customer has no membership (for showing non-member CTA)
     * @returns {boolean} True if customer has no active membership
     */
    isNonMember = () => {
      try {
        const customerMembership = this.getCustomerMembership();
        return !customerMembership || !customerMembership.active;
      } catch (error) {
        console.error("❌ CYO: Error checking non-member status:", error);
        return true; // Default to non-member if we can't determine
      }
    };

    /**
     * Get pagination settings for a specific step
     * @param {Object} step - The step to get pagination settings for
     * @returns {Object} Pagination configuration for the step
     */
    getStepPaginationSettings = (step) => {
      try {
        // Check if step has its own pagination settings
        if (step.pagination) {
          return {
            enabled: step.pagination.enabled ?? this.defaultProductOptionsPagination.enabled,
            optionsPerPage: step.pagination.options_per_page ?? this.defaultProductOptionsPagination.optionsPerPage,
            viewMoreCta: step.pagination.view_more_cta ?? this.defaultProductOptionsPagination.viewMoreCta ?? "MORE OPTIONS +",
            viewLessCta: step.pagination.view_less_cta ?? this.defaultProductOptionsPagination.viewLessCta ?? "LESS OPTIONS -",
          };
        }

        // Fall back to default pagination settings
        return {
          ...this.defaultProductOptionsPagination,
          viewMoreCta: this.defaultProductOptionsPagination.viewMoreCta ?? "MORE OPTIONS +",
          viewLessCta: this.defaultProductOptionsPagination.viewLessCta ?? "LESS OPTIONS -",
        };
      } catch (error) {
        console.error("❌ CYO: Error getting step pagination settings:", error);
        return {
          ...this.defaultProductOptionsPagination,
          viewMoreCta: "MORE OPTIONS +",
          viewLessCta: "LESS OPTIONS -",
        };
      }
    };

    /**
     * Main render method - builds the complete UI structure in shadow DOM
     */
    render = async () => {
      try {
        if (this.allowDebug()) console.log("🎨 CYO: Starting render");

        // Update progress for screen readers
        const stepForProgress = this.getCurrentStep();
        const totalSteps = this.config.steps.length;
        if (this.isReviewStep()) {
          this.updateProgress(
            totalSteps + 1,
            totalSteps + 1,
            "Review Your Selections"
          );
          this.announceToScreenReader(
            "Now reviewing your selections. You can modify any step or add to bag."
          );
        } else {
          const stepName =
            stepForProgress?.label || `Step ${this.currentStepIndex + 1}`;
          this.updateProgress(this.currentStepIndex + 1, totalSteps, stepName);
          this.announceToScreenReader(
            `${stepName}. Make your selection to continue.`
          );
        }

        let htmlContent;
        if (this.isReviewStep()) {
          if (this.allowDebug()) console.log("🎨 CYO: Rendering review step");
          htmlContent = await this.renderReviewStep();
        } else {
          if (this.allowDebug()) console.log("🎨 CYO: Rendering current step");
          htmlContent = this.renderCurrentStep();
        }

        // Add content to the container
        const container = this.shadowRoot.getElementById("cyo-content");
        if (container) {
          container.replaceChildren();
          const content =
            createElementFromHTML(htmlContent);
          if (content) {
            container.appendChild(content);
          }
        }

        this.attachEventListeners();

        // Check if current step already has a selection and restore UI state
        const currentStep = this.getCurrentStep();
        const existingSelection = this.getSelectionForStep(currentStep);

        if (existingSelection) {
          if (this.allowDebug()) {
            console.log(
              `🔄 CYO: Restoring existing selection for step "${currentStep.label}": ${existingSelection.product.title}`
            );
          }

          // Use setTimeout to ensure DOM is ready before restoring state
          setTimeout(() => {
            this.restoreStepState(existingSelection);
          }, 0);
        } else if (!this.isReviewStep() && this.selectFirstAvailableOption) {
          // Auto-select first available product only if no existing selection
          const selectionZones = this.getAvailableOptions(currentStep);

          // Try regular zone first, then members only zone
          const zonesToCheck = ['regular', 'members_only'];

          for (const zoneKey of zonesToCheck) {
            if (selectionZones[zoneKey] && selectionZones[zoneKey].options.length > 0) {
              const firstOption = selectionZones[zoneKey].options[0];
              const firstProduct = firstOption.products.find(
                (product) =>
                  this.isProductAvailable(product) &&
                  this.getAvailableVariants(product, firstOption).length > 0
              );

              if (firstProduct) {
                if (this.allowDebug()) {
                  console.log(
                    `🤖 CYO: Auto-selecting first available product: ${firstProduct.title} from ${zoneKey} zone`
                  );
                }
                // Use setTimeout to ensure DOM is ready
                setTimeout(() => {
                  this.handleProductSelection(firstProduct.id, firstOption.id, zoneKey);
                }, 0);
                break; // Stop after first successful auto-selection
              }
            }
          }
        }
      } catch (error) {
        console.error("❌ CYO: Error in render:", error);
        this.renderError("Failed to render component");
      }
    };

    /**
     * Render the current step UI
     */
    renderCurrentStep = () => {
      try {
        const currentStep = this.getCurrentStep();
        if (!currentStep) {
          if (this.allowDebug()) console.warn("⚠️ CYO: No current step found");
          return '<div class="cyo-error">No current step found</div>';
        }

        if (this.allowDebug())
          console.log(`🎨 CYO: Rendering step "${currentStep.label}"`);

        const selectionZones = this.getAvailableOptions(currentStep);
        if (Object.keys(selectionZones).length === 0) {
          if (this.allowDebug())
            console.warn(
              `⚠️ CYO: No available selection zones for step "${currentStep.label}"`
            );
          return this.renderUnavailableMessage();
        }

        if (this.allowDebug())
          console.log(
            `🎨 CYO: Found ${Object.keys(selectionZones).length} selection zones:`,
            Object.keys(selectionZones)
          );

        // Render all selection zones
        const selectionZonesHTML = Object.entries(selectionZones)
          .map(([zoneKey, zone]) => this.renderSelectionZone(zoneKey, zone))
          .join('');

        return `
          <div class="cyo-step">
            <div class="cyo-step__header">
              <h2 class="cyo-step__title">${this.currentStepIndex + 1}. ${
          currentStep.label
        }</h2>
              ${this.renderHeaderNavigation()}
            </div>
            <div class="cyo-step__content">
              ${selectionZonesHTML}
            </div>
            <div class="cyo-cart-error-container" style="display: none;"></div>
            <div class="cyo-step__actions">
              ${this.renderActionButtons()}
            </div>
          </div>
        `;
      } catch (error) {
        console.error("❌ CYO: Error rendering current step:", error);
        return '<div class="cyo-error">Failed to render step</div>';
      }
    };

    /**
     * Get available options for a step (shows all variants, including unavailable ones)
     * Now returns an object with selection zones instead of a flat array
     */
    getAvailableOptions = (step) => {
      try {
        const selectionZones = {};

        // Process regular options
        if (step.options && step.options.length > 0) {
          const availableOptions = step.options.filter((option) => {
            const hasAnyProducts = option.products.some(
              (product) =>
                this.isProductAvailable(product) &&
                this.getAvailableVariants(product, option).length > 0
            );

            // Also include options that have products with variants (even if unavailable)
            const hasProductsWithVariants = option.products.some(
              (product) => product.variants && product.variants.length > 0
            );

            return hasAnyProducts || hasProductsWithVariants;
          });

          if (availableOptions.length > 0) {
            selectionZones.regular = {
              type: 'regular',
              label: step.name || 'Style',
              options: availableOptions
            };
          }

          if (this.allowDebug())
            console.log(
              `✅ CYO: ${availableOptions.length}/${step.options.length} regular options available`
            );
        }

        // Process members only options with membership validation
        if (step.members_only_options && step.members_only_options.length > 0) {
          const membersOptionsWithEligibility = step.members_only_options.map((option) => {
            // Check if customer meets membership requirements for this option
            const hasValidMembership = this.validateMembershipRequirement(option.membership);

            // Check product availability as usual
            const hasAvailableProducts = option.products.some(
              (product) =>
                this.isProductAvailable(product) &&
                this.getAvailableVariants(product, option).length > 0
            );

            // Also include options that have products with variants (even if unavailable)
            const hasProductsWithVariants = option.products.some(
              (product) => product.variants && product.variants.length > 0
            );

            // Return option with eligibility info
            return {
              ...option,
              isEligible: hasValidMembership,
              hasAvailableProducts: hasAvailableProducts || hasProductsWithVariants
            };
          });

          // Filter to only show options that have available products or products with variants
          const availableMembersOptions = membersOptionsWithEligibility.filter(
            option => option.hasAvailableProducts
          );

          if (availableMembersOptions.length > 0) {
            selectionZones.members_only = {
              type: 'members_only',
              label: step.members_only_name || 'Style', // This goes in cyo-variant-option__label
              title: step.members_only_label || 'Members Only', // This goes in cyo-step__title
              non_members_cta: step.members_only_non_members_cta || '',
              options: availableMembersOptions, // Includes eligibility info
              showNonMemberCta: this.isNonMember()
            };
          }

          if (this.allowDebug()) {
            const eligibleCount = availableMembersOptions.filter(opt => opt.isEligible).length;
            console.log(
              `✅ CYO: ${availableMembersOptions.length}/${step.members_only_options.length} members only options available (${eligibleCount} eligible for customer)`
            );
          }
        }

        return selectionZones;
      } catch (error) {
        console.error("❌ CYO: Error getting available options:", error);
        return {};
      }
    };

    /**
     * Render a selection zone (regular or members_only)
     */
    renderSelectionZone = (zoneKey, zone) => {
      try {
        // Render zone title if provided (for members_only zones)
        const zoneTitle = zone.title ?
          `<div class="cyo-selection-zone__header"><h3 class="cyo-selection-zone__title">${zone.title}</h3>${window.theme?.elements?.membership_icons?.default}</div>` : '';

        // Render non-member CTA only if this is a members only zone and customer should see it
        const nonMemberCTA = zone.type === 'members_only' && zone.showNonMemberCta && zone.non_members_cta ?
          `<div class="cyo-non-member-cta">${zone.non_members_cta}</div>` : '';

        // Always render product options container if there are options (even if disabled)
        const productOptionsHTML = zone.options && zone.options.length > 0 ?
          `<div class="cyo-variant-option">
             <label class="cyo-variant-option__label">${zone.label} - <span class="cyo-step__selection-name"></span></label>
             ${this.renderProductOptions(zone.options, zone.type)}
           </div>` : '';

        return `
          <div class="cyo-selection-zone cyo-selection-zone--${zone.type}" data-zone-type="${zone.type}" data-zone-key="${zoneKey}">
            ${zoneTitle}
            ${productOptionsHTML}
            <div class="cyo-variant-selection" data-zone-key="${zoneKey}"></div>
            ${nonMemberCTA}
          </div>
        `;
      } catch (error) {
        console.error(`❌ CYO: Error rendering selection zone ${zoneKey}:`, error);
        return '';
      }
    };

    /**
     * Render product options using radio inputs
     */
    renderProductOptions = (options, zoneType = 'regular') => {
      const currentStep = this.getCurrentStep();
      const currentSelection = this.getSelectionForStep(currentStep);
      const fieldsetId = `product-options-${currentStep.id}-${zoneType}`;
      const zoneKey = `${currentStep.id}-${zoneType}`;

      // Get step-specific pagination settings
      const paginationSettings = this.getStepPaginationSettings(currentStep);

      // Flatten all products from all options (include products with variants even if unavailable)
      const allProducts = [];
      options.forEach((option) => {
        const products = option.products.filter(
          (product) =>
            this.isProductAvailable(product) &&
            this.getAvailableVariants(product, option).length > 0
        );

        // Also include products that have variants but are unavailable
        const unavailableProducts = option.products.filter(
          (product) =>
            product.variants && product.variants.length > 0 &&
            (!this.isProductAvailable(product) || this.getAvailableVariants(product, option).length === 0)
        );

        // Combine available and unavailable products
        [...products, ...unavailableProducts].forEach((product) => allProducts.push({ product, option }));
      });

      // Determine how many products to show for this specific zone
      const totalProducts = allProducts.length;
      const isZoneExpanded = this.zoneExpansionState[zoneKey] || false;

      // Check if pagination should be available based on step configuration
      const shouldShowPagination =
        paginationSettings.enabled &&
        totalProducts > paginationSettings.optionsPerPage;

      const showCount = isZoneExpanded
        ? totalProducts
        : shouldShowPagination
        ? Math.min(paginationSettings.optionsPerPage, totalProducts)
        : totalProducts;
      const productsToShow = allProducts.slice(0, showCount);

      const hasMoreProducts =
        shouldShowPagination &&
        totalProducts > paginationSettings.optionsPerPage;

      if (this.allowDebug()) {
        console.log(
          `🎨 CYO: Zone ${zoneType} - Showing ${showCount}/${totalProducts} products, expanded: ${isZoneExpanded}`
        );
      }

      // Generate HTML for visible products
      const productOptionsHTML = productsToShow
        .map(({ product, option }) => {
          const isSelected =
            currentSelection &&
            currentSelection.product.id === product.id &&
            currentSelection.option.id === option.id &&
            currentSelection.zoneType === zoneType;

          const inputId = `${fieldsetId}-${zoneType}-product-${product.id}-option-${option.id}`;
          const radioName = `${fieldsetId}-${zoneType}-selection`;

          // Check if this option is eligible (for members-only zones)
          const isEligible = zoneType === 'members_only' ? (option.isEligible !== false) : true;

          // Check if this product has available variants
          const availableVariants = this.getAvailableVariants(product, option);
          const hasAvailableVariants = availableVariants.length > 0;

          // Product is disabled only if not eligible (not available variants)
          const isDisabled = !isEligible;

          // Add classes for disabled state and sold out state
          const optionClasses = `cyo-product-option${isDisabled ? ' cyo-product-option--disabled' : ''}${!hasAvailableVariants ? ' cyo-product-option--sold-out' : ''}`;
          const inputClasses = `cyo-product-option__input${isDisabled ? ' cyo-product-option__input--disabled' : ''}`;

          return `
          <div class="${optionClasses}">
            <input
              type="radio"
              class="${inputClasses}"
              id="${inputId}"
              name="${radioName}"
              value="${product.id}-${option.id}"
              data-product-id="${product.id}"
              data-option-id="${option.id}"
              data-zone-type="${zoneType}"
              data-is-eligible="${isEligible}"
              data-has-available-variants="${hasAvailableVariants}"
              ${isSelected ? "checked" : ""}
              ${isDisabled ? "disabled" : ""}
            />
            <label for="${inputId}" class="cyo-product-option__label">
              ${product.media[0] || ""}
              <div class="cyo-product-option__title">${product.title}</div>
            </label>
          </div>
        `;
        })
        .join("");

      // Generate MORE/LESS buttons with zone-specific data and custom CTA text
      const toggleButtonHTML = hasMoreProducts
        ? `
        <div class="cyo-product-options-toggle" data-zone-key="${zoneKey}">
          ${
            isZoneExpanded
              ? `<button type="button" class="cyo-more-options-btn cyo-more-options-btn--show-less" data-zone-key="${zoneKey}" data-zone-type="${zoneType}">
              <span>${paginationSettings.viewLessCta}</span>
            </button>`
              : `<button type="button" class="cyo-more-options-btn cyo-more-options-btn--show-more" data-zone-key="${zoneKey}" data-zone-type="${zoneType}">
              <span>${paginationSettings.viewMoreCta}</span>
            </button>`
          }
        </div>
      `
        : "";

      return `
        <fieldset class="cyo-variant-option__fieldset">
          <legend class="visually-hidden">Select a product option</legend>
          <div class="cyo-product-options">
            ${productOptionsHTML}
          </div>
          ${toggleButtonHTML}
        </fieldset>
      `;
    };

    /**
     * Check if an option selector should be shown based on allowed_option_selectors configuration
     * @param {string} optionName - The option name to check (e.g., "Color", "Size")
     * @returns {boolean} True if the option is allowed to be shown
     */
    isOptionSelectorAllowed = (optionName) => {
      try {
        const currentStep = this.getCurrentStep();
        if (!currentStep || !currentStep.allowed_option_selectors) {
          // If no configuration, default to showing all options
          return true;
        }

        const allowed = currentStep.allowed_option_selectors.includes(optionName);

        if (this.allowDebug()) {
          console.log(
            `🎛️ CYO: General option check - ${optionName}: ${
              allowed ? "ALLOWED" : "HIDDEN"
            }`
          );
        }

        return allowed;
      } catch (error) {
        console.error("❌ CYO: Error checking option selector allowance:", error);
        return true; // Default to showing on error
      }
    };

    /**
     * Check if a single option selector should be shown based on step configuration
     * Now includes two-step evaluation: allowed_option_selectors first, then allowed_single_option_selectors
     * @param {string} optionName - The option name to check (e.g., "Color", "Size")
     * @param {number} optionCount - Number of available options
     * @returns {boolean} True if the selector should be shown
     */
    shouldShowSingleOptionSelector = (optionName, optionCount) => {
      try {
        // Step 1: Check if option is generally allowed (applies to all options)
        if (!this.isOptionSelectorAllowed(optionName)) {
          if (this.allowDebug()) {
            console.log(
              `🎛️ CYO: Option ${optionName} hidden by allowed_option_selectors`
            );
          }
          return false;
        }

        // Step 2: If there are multiple options, show (since it passed step 1)
        if (optionCount > 1) {
          if (this.allowDebug()) {
            console.log(
              `🎛️ CYO: Option ${optionName} shown (${optionCount} options available)`
            );
          }
          return true;
        }

        // Step 3: If only one option, check allowed_single_option_selectors
        const currentStep = this.getCurrentStep();
        if (!currentStep || !currentStep.allowed_single_option_selectors) {
          // Default behavior: hide single options if no configuration
          if (this.allowDebug()) {
            console.log(
              `🎛️ CYO: Single option ${optionName} hidden (no allowed_single_option_selectors config)`
            );
          }
          return false;
        }

        const allowed =
          currentStep.allowed_single_option_selectors.includes(optionName);

        if (this.allowDebug()) {
          console.log(
            `🎛️ CYO: Single option check - ${optionName} (${optionCount} options): ${
              allowed ? "SHOW" : "HIDE"
            }`
          );
        }

        return allowed;
      } catch (error) {
        console.error("❌ CYO: Error checking single option selector:", error);
        return false;
      }
    };

    /**
     * Discover all option types available on a product (with caching)
     * @param {Object} product - The product object
     * @returns {Array} Array of option type objects with metadata
     */
    getProductOptionTypes = (product) => {
      try {
        // Check cache first
        if (this.#productOptionTypesCache.has(product.id)) {
          return this.#productOptionTypesCache.get(product.id);
        }

        const optionTypes = [];

        // Use product.options array as the source of truth for available options
        product.options.forEach((optionName, index) => {
          const optionKey = optionName.toLowerCase();

          // Determine render type and special handling
          let renderType = "button"; // Default for all options
          let specialKey = optionKey;

          // Special handling for color - gets swatch rendering
          if (optionKey === "color") {
            renderType = "swatch";
            // Use special color properties if available, otherwise fall back to options array
            if (
              product.has_color_option &&
              product.color_option_index !== undefined
            ) {
              specialKey = "color"; // Will use variant.color property
            }
          }
          // Special handling for size - check for special properties
          else if (optionKey === "size") {
            if (
              product.has_size_option &&
              product.size_option_index !== undefined
            ) {
              specialKey = "size"; // Will use size-specific logic
            }
          }

          optionTypes.push({
            key: optionKey,
            name: optionName,
            index: index,
            type: renderType,
            specialKey: specialKey, // Used to determine how to extract values
          });
        });

        // Cache the result
        this.#productOptionTypesCache.set(product.id, optionTypes);

        if (this.allowDebug()) {
          console.log(
            `🔍 CYO: Discovered ${optionTypes.length} option types for product ${product.id}:`,
            optionTypes
          );
        }

        return optionTypes;
      } catch (error) {
        console.error("❌ CYO: Error discovering product option types:", error);
        return [];
      }
    };

    /**
     * Render an individual option group (color, size, etc.)
     * Now shows all variant options, including unavailable ones marked as sold out
     * @param {Object} product - The product object
     * @param {Object} selectionOption - The selection option with allowed values
     * @param {Object} optionType - The option type metadata
     * @param {Array} allVariants - All variants for this product (available and unavailable)
     * @param {Object} preSelectedVariant - Pre-selected variant if any
     * @returns {string} HTML for the option group
     */
    renderOptionGroup = (
      product,
      selectionOption,
      optionType,
      allVariants,
      preSelectedVariant
    ) => {
      try {
        const optionKey = optionType.key;
        const optionName = optionType.name;
        const optionIndex = optionType.index;
        const renderType = optionType.type;

        // Get actual available variants for checking availability
        const availableVariants = this.getAvailableVariants(product, selectionOption);

        // Get all possible values for this option type (from all variants)
        let allValues;
        let selectedValue = null;

        if (
          optionType.specialKey === "color" &&
          product.has_color_option &&
          product.color_option_index !== undefined
        ) {
          // For color with special properties, use the color property directly
          allValues = [
            ...new Set(allVariants.map((v) => v.color).filter(Boolean)),
          ];
          selectedValue = preSelectedVariant?.color;

          // Apply color restrictions from selection option (case-insensitive)
          if (
            selectionOption.allowed_colors &&
            selectionOption.allowed_colors.length > 0
          ) {
            const normalizedAllowedColors = selectionOption.allowed_colors.map(c => c.toUpperCase());
            allValues = allValues.filter((value) =>
              normalizedAllowedColors.includes(value.toUpperCase())
            );
          }
        } else if (
          optionType.specialKey === "size" &&
          product.has_size_option &&
          product.size_option_index !== undefined
        ) {
          // For size with special properties, use the size-specific index
          const sizeIndex = product.size_option_index;
          allValues = [
            ...new Set(
              allVariants.map((v) => v.options[sizeIndex]).filter(Boolean)
            ),
          ];
          selectedValue = preSelectedVariant?.options?.[sizeIndex];

          // Apply size restrictions from selection option (case-insensitive)
          if (
            selectionOption.allowed_sizes &&
            selectionOption.allowed_sizes.length > 0
          ) {
            const normalizedAllowedSizes = selectionOption.allowed_sizes.map(s => s.toUpperCase());
            allValues = allValues.filter((value) =>
              normalizedAllowedSizes.includes(value.toUpperCase())
            );
          }
        } else {
          // For all other option types (including color/size without special properties), use the option array index
          allValues = [
            ...new Set(
              allVariants
                .map((v) => v.options[optionIndex])
                .filter(Boolean)
            ),
          ];
          selectedValue = preSelectedVariant?.options?.[optionIndex];

          // Apply restrictions if available - only check for known restriction patterns (case-insensitive)
          if (
            optionKey === "color" &&
            selectionOption.allowed_colors &&
            selectionOption.allowed_colors.length > 0
          ) {
            const normalizedAllowedColors = selectionOption.allowed_colors.map(c => c.toUpperCase());
            allValues = allValues.filter((value) =>
              normalizedAllowedColors.includes(value.toUpperCase())
            );
          } else if (
            optionKey === "size" &&
            selectionOption.allowed_sizes &&
            selectionOption.allowed_sizes.length > 0
          ) {
            const normalizedAllowedSizes = selectionOption.allowed_sizes.map(s => s.toUpperCase());
            allValues = allValues.filter((value) =>
              normalizedAllowedSizes.includes(value.toUpperCase())
            );
          }
          // For any other option types, check for generic allowed_{option_key}s pattern
          else {
            const allowedKey = `allowed_${optionKey}s`;
            if (
              selectionOption[allowedKey] &&
              selectionOption[allowedKey].length > 0
            ) {
              allValues = allValues.filter((value) =>
                selectionOption[allowedKey].includes(value)
              );
            }
          }
        }

        // Skip if no values at all
        if (allValues.length === 0) return "";

        // Check if this option selector should be shown
        const shouldShow = this.shouldShowSingleOptionSelector(
          optionName,
          allValues.length
        );
        const hideClass = shouldShow ? "" : "cyo-variant-option--hidden";

        const fieldsetId = `${optionKey}-selection-${product.id}`;
        const dataAttr = `data-${optionKey}`;

        if (renderType === "swatch" && optionKey === "color") {
          // Render color swatches
          return `
            <div class="cyo-variant-option ${hideClass}">
              <label class="cyo-variant-option__label">${optionName} - <span class="cyo-step__selection-name" data-option-type="${optionKey}"></span></label>
              <fieldset class="cyo-variant-option__fieldset">
                <legend class="visually-hidden">${optionName} selection</legend>
                <div class="cyo-color-swatches">
                  ${allValues
                    .map((value) => {
                      const valueHandle = value
                        .toLowerCase()
                        .replace(/[^a-z0-9]/g, "-");
                      const inputId = `${fieldsetId}-${valueHandle}`;
                      const isSelected = value === selectedValue;

                      // Check if this value has any available variants
                      const hasAvailableVariants = availableVariants.some(v =>
                        (optionType.specialKey === "color" && product.has_color_option)
                          ? v.color === value
                          : v.options[optionIndex] === value
                      );
                      const isSoldOut = !hasAvailableVariants;

                      return `
                        <div class="cyo-color-option ${
                          isSelected ? "cyo-color-option--selected" : ""
                        } ${isSoldOut ? "cyo-color-option--sold-out" : ""}">
                          <input
                            type="radio"
                            class="cyo-color-option__input ${isSoldOut ? "cyo-color-option__input--unavailable" : ""}"
                            id="${inputId}"
                            name="${fieldsetId}"
                            value="${value}"
                            ${dataAttr}="${value}"
                            ${isSelected ? "checked" : ""}
                          />
                          <label for="${inputId}" class="cyo-color-option__label">
                            <div class="cyo-color-option__swatch" style="background: var(--swatch-${valueHandle});"></div>
                            <span class="visually-hidden">${value}</span>
                          </label>
                        </div>
                      `;
                    })
                    .join("")}
                </div>
              </fieldset>
            </div>
          `;
        } else {
          // Render button-style options (size and other option types)
          const optionClass =
            optionKey === "size"
              ? "cyo-size-options"
              : `cyo-${optionKey}-options`;
          const itemClass =
            optionKey === "size"
              ? "cyo-size-option"
              : `cyo-${optionKey}-option`;

          return `
            <div class="cyo-variant-option ${hideClass}">
              <label class="cyo-variant-option__label">${optionName} - <span class="cyo-step__selection-name" data-option-type="${optionKey}"></span></label>
              <fieldset class="cyo-variant-option__fieldset">
                <legend class="visually-hidden">${optionName} selection</legend>
                <div class="${optionClass}">
                  ${allValues
                    .map((value) => {
                      const inputId = `${fieldsetId}-${value
                        .toLowerCase()
                        .replace(/[^a-z0-9]/g, "-")}`;
                      const isSelected = value === selectedValue;

                      // Check if this value has any available variants
                      const hasAvailableVariants = availableVariants.some(v =>
                        (optionType.specialKey === "size" && product.has_size_option)
                          ? v.options[product.size_option_index] === value
                          : v.options[optionIndex] === value
                      );
                      const isSoldOut = !hasAvailableVariants;

                      return `
                        <div class="${itemClass} ${
                        isSelected ? `${itemClass}--selected` : ""
                      } ${isSoldOut ? `${itemClass}--sold-out` : ""}">
                          <input
                            type="radio"
                            class="${itemClass}__input ${isSoldOut ? `${itemClass}__input--unavailable` : ""}"
                            id="${inputId}"
                            name="${fieldsetId}"
                            value="${value}"
                            ${dataAttr}="${value}"
                            ${isSelected ? "checked" : ""}
                          />
                          <label for="${inputId}" class="${itemClass}__label">${value}</label>
                        </div>
                      `;
                    })
                    .join("")}
                </div>
              </fieldset>
            </div>
          `;
        }
      } catch (error) {
        console.error(
          `❌ CYO: Error rendering option group for ${optionType.key}:`,
          error
        );
        return "";
      }
    };

    /**
     * Render variant selection for selected product using radio inputs
     * Now shows all variants, including unavailable ones marked as sold out
     */
    renderVariantSelection = (product, selection, zoneKey = null) => {
      const option = selection.option;
      const availableVariants = this.getAvailableVariants(product, option);

      // Get all variants (available and unavailable)
      const allVariants = product.variants || [];

      if (allVariants.length === 0) return;

      // Target specific zone's variant selection container if zoneKey is provided
      const selector = zoneKey
        ? `.cyo-variant-selection[data-zone-key="${zoneKey}"]`
        : ".cyo-variant-selection";

      const variantContainer = this.shadowRoot.querySelector(selector);
      if (!variantContainer) return;

      // Check if we have a pre-selected variant
      const preSelectedVariant = selection.variant;

      let variantHTML = "";

      // Discover all option types from the product
      const optionTypes = this.getProductOptionTypes(product);

      // Render each option type dynamically
      for (const optionType of optionTypes) {
        const optionHTML = this.renderOptionGroup(
          product,
          option,
          optionType,
          allVariants, // Pass all variants instead of just available ones
          preSelectedVariant
        );
        variantHTML += optionHTML;
      }

      variantContainer.replaceChildren();
      if (variantHTML.trim()) {
        const content = createElementFromHTML(
          `<div>${variantHTML}</div>`
        );
        if (content) {
          // Move the children of the wrapper div to the container
          while (content.firstChild) {
            variantContainer.appendChild(content.firstChild);
          }
        }
      }

      // Handle auto-selection for first available option (only if no pre-selection)
      if (
        !preSelectedVariant &&
        this.selectFirstAvailableOption &&
        availableVariants.length >= 1
      ) {
        if (this.allowDebug())
          console.log(
            `🤖 CYO: Auto-selecting first of ${availableVariants.length} available variants`
          );
        this.handleVariantSelection(availableVariants[0], selection);
      } else if (preSelectedVariant) {
        // If we have a pre-selected variant, make sure the action buttons and media are updated
        this.updateActionButtons();
        this.debouncedUpdateMediaGallery();
      } else {
        // No variant selected yet, but update display to show product name
        this.updateOptionValueDisplay();
      }
    };

    /**
     * Render header navigation (desktop only)
     * Now checks if selected variant is available and shows "SOLD OUT" if not
     * @param {boolean} wrapperOnly - If true, returns only the inner content without the wrapper div
     */
    renderHeaderNavigation = (wrapperOnly = false) => {
      const currentStep = this.getCurrentStep();
      const selection = this.getSelectionForStep(currentStep);

      // Check if we have a selection and if it's available
      const hasSelection = selection && selection.variant;
      const isVariantAvailable = hasSelection ? this.isVariantAvailable(selection.variant) : false;

      // Can proceed only if we have a selection AND the variant is available
      const canProceed = hasSelection && isVariantAvailable;

      const showBack = this.currentStepIndex > 0 || this.currentPath.length > 0;

      let nextLabel = "Next";
      let backLabel = "Back";

      if (this.isReviewStep()) {
        nextLabel = "Add to Bag";
      } else if (
        this.currentStepIndex === this.config.steps.length - 1 &&
        this.currentPath.length === 0
      ) {
        nextLabel = "Review";
      } else if (this.currentStepIndex < this.config.steps.length - 1) {
        nextLabel = this.config.steps[this.currentStepIndex + 1].name;
      }

      // If we have a selection but the variant is sold out, show "SOLD OUT"
      if (hasSelection && !isVariantAvailable) {
        nextLabel = "SOLD OUT";
      }

      // Get back label
      if (showBack) {
        if (this.currentStepIndex === 1) {
          backLabel = this.config.steps[0].name;
        } else if (this.currentStepIndex > 1) {
          backLabel = this.config.steps[this.currentStepIndex - 1].name;
        }
      }

      const innerContent = `
          ${
            showBack
              ? `<button class="cyo-step__nav-item cyo-step__nav-item--back" type="button">
                  <span class="cyo-step__nav-arrow">
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                      <path d="M7 11L2 6L7 1" stroke="#414142" stroke-width="1.5"/>
                    </svg>
                  </span>
                  <span class="cyo-step__nav-text">Back: ${backLabel}</span>
                  <div class="loading-overlay__spinner hidden">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                  </div>
                </button>`
              : ""
          }
          ${
            !this.isReviewStep()
              ? `<button class="cyo-step__nav-item cyo-step__nav-item--next ${
                  canProceed ? "" : "disabled"
                }" ${!canProceed ? "disabled" : ""} type="button">
                  <span class="cyo-step__nav-text">Next: ${nextLabel}</span>
                  <span class="cyo-step__nav-arrow">
                    <svg xmlns="http://www.w3.org/2000/svg" width="8" height="12" viewBox="0 0 8 12" fill="none">
                      <path d="M1 1L6 6L1 11" stroke="#414142" stroke-width="1.5"/>
                    </svg>
                  </span>
                  <div class="loading-overlay__spinner hidden">
                    <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                      <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                    </svg>
                  </div>
                </button>`
              : ""
          }
      `;

      if (wrapperOnly) {
        return innerContent;
      }

      return `<div class="cyo-step__nav">${innerContent}</div>`;
    };

    /**
     * Render action buttons
     * Now checks if selected variant is available and shows "SOLD OUT" if not
     */
    renderActionButtons = () => {
      const currentStep = this.getCurrentStep();
      const selection = this.getSelectionForStep(currentStep);
      const isReview = this.isReviewStep();

      // Check if we have a selection and if it's available
      const hasSelection = selection && selection.variant;
      const isVariantAvailable = hasSelection ? this.isVariantAvailable(selection.variant) : false;

      // Can proceed only if we have a selection AND the variant is available
      const canProceed = isReview
        ? this.selections.size > 0
        : hasSelection && isVariantAvailable;

      const showBack = this.currentStepIndex > 0 || this.currentPath.length > 0;

      let nextLabel = "Next";
      let nextButtonClass = "cyo-step__action--next";

      if (isReview) {
        nextLabel = "Add to Bag";
        nextButtonClass = "cyo-step__action--add-to-bag";
      } else if (
        this.currentStepIndex === this.config.steps.length - 1 &&
        this.currentPath.length === 0
      ) {
        nextLabel = "Next: Review";
      } else if (this.currentStepIndex < this.config.steps.length - 1) {
        nextLabel = `Next: ${
          this.config.steps[this.currentStepIndex + 1].name
        }`;
      }

      // If we have a selection but the variant is sold out, show "SOLD OUT"
      if (hasSelection && !isVariantAvailable) {
        nextLabel = "SOLD OUT";
        nextButtonClass = "cyo-step__action--sold-out";
      }

      return `
        ${
          showBack
            ? `<button class="cyo-step__action cyo-step__action--back">
                <span class="cyo-step__action-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="11" height="17" viewBox="0 0 11 17" fill="none">
                    <path d="M9.5 16L2 8.5L9.5 0.999998" stroke="white" stroke-width="1.5"/>
                  </svg>
                </span>
                <span class="cyo-step__action-text">Back</span>
                <div class="loading-overlay__spinner hidden">
                  <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                  </svg>
                </div>
              </button>`
            : ""
        }
        <button class="cyo-step__action ${nextButtonClass} ${
        canProceed ? "" : "disabled"
      }" ${!canProceed ? "disabled" : ""}>
          <span class="cyo-step__action-text">${nextLabel}</span>
          <div class="loading-overlay__spinner hidden">
            <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
              <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
            </svg>
          </div>
        </button>
      `;
    };

    /**
     * Render review step
     */
    renderReviewStep = async () => {
      try {
        const selections = Array.from(this.selections.values());

        if (selections.length === 0) {
          return '<div class="cyo-error">No selections found</div>';
        }

        await this.calculatePricing();

        // Generate visual step summaries
        const stepSummaries = await this.renderStepSummaries();

        return `
          <div class="cyo-step">
            <div class="cyo-step__header">
              <h2 class="cyo-step__title">${
                this.config.steps.length + 1
              }. Verify Your Selection</h2>
              ${this.renderHeaderNavigation()}
            </div>
            <div class="cyo-step__content">
              ${stepSummaries}
            </div>
            <div class="cyo-cart-error-container" style="display: none;"></div>
            <div class="cyo-step__actions">
              ${this.renderActionButtons()}
            </div>
          </div>
        `;
      } catch (error) {
        console.error("❌ CYO: Error rendering review step:", error);
        return '<div class="cyo-error">Failed to render review</div>';
      }
    };

    /**
     * Render visual summaries of all step selections
     */
    renderStepSummaries = async () => {
      try {
        const summaries = [];

        // Iterate through each step in order
        for (const step of this.config.steps) {
          const selection = this.getSelectionForStep(step);

          if (selection && selection.variant) {
            const summary = await this.renderStepSummary(step, selection);
            if (summary) {
              summaries.push(summary);
            }
          }
        }

        return summaries.join("");
      } catch (error) {
        console.error("❌ CYO: Error rendering step summaries:", error);
        return '<div class="cyo-error">Failed to render step summaries</div>';
      }
    };

    /**
     * Get variant-specific media HTML
     * @param {Object} variant - The variant object with media_url
     * @returns {Promise<string|null>} HTML string for the variant media or null if failed
     */
    getVariantMediaHtml = async (variant) => {
      try {
        if (!variant || !variant.media_url) {
          return null;
        }

        const response = await fetch(variant.media_url);
        if (!response.ok) {
          return null;
        }

        const mediaHtml = await response.text();

        // Extract the first img tag from the response
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = mediaHtml;
        const imgElement = tempDiv.querySelector('img');

        if (imgElement) {
          return imgElement.outerHTML;
        }

        return null;
      } catch (error) {
        console.error("❌ CYO: Error fetching variant media HTML:", error);
        return null;
      }
    };

    /**
     * Render a visual summary for a single step selection
     */
    renderStepSummary = async (step, selection) => {
      try {
        const product = selection.product;
        const option = selection.option;
        const variant = selection.variant;

        if (!product || !variant) return "";

        // Generate unique IDs for this review step render
        const reviewStepId = `review-step-${step.id}`;
        const reviewProductId = `review-product-${product.id}`;

        // Get product option types for rendering variant selections
        const optionTypes = this.getProductOptionTypes(product);

        // Get the appropriate media for the selected variant
        let productMediaHtml = product.media[0] || "";

        // If variant has media_url, fetch and use variant-specific media
        if (variant && variant.media_url) {
          try {
            // For the review step, we'll use the variant media if available
            const variantMediaHtml = await this.getVariantMediaHtml(variant);
            if (variantMediaHtml) {
              productMediaHtml = variantMediaHtml;
              if (this.allowDebug()) {
                console.log(
                  `🖼️ CYO: Review step using variant media for ${product.title} - ${variant.title}`
                );
              }
            } else if (this.allowDebug()) {
              console.log(
                `🖼️ CYO: Review step falling back to default media for ${product.title}`
              );
            }
          } catch (error) {
            console.error("❌ CYO: Error getting variant media for review:", error);
            // Fall back to default product media
          }
        }

        // Render the product swatch (always shown in review)
        const productSwatchHtml = `
          <div class="cyo-variant-option">
            <label class="cyo-variant-option__label">${
              step.name || "Style"
            } - <span class="cyo-step__selection-name">${product.title.toLowerCase()}</span></label>
            <fieldset class="cyo-variant-option__fieldset">
              <legend class="visually-hidden">Selected product option</legend>
              <div class="cyo-product-options">
                <div class="cyo-product-option">
                  <input type="radio" class="cyo-product-option__input" id="${reviewProductId}" name="${reviewStepId}-product" checked disabled />
                  <label for="${reviewProductId}" class="cyo-product-option__label">
                    ${productMediaHtml}
                  </label>
                </div>
              </div>
            </fieldset>
          </div>
        `;

        // Render variant option selections based on allowed_option_selectors and allowed_single_option_selectors
        let variantOptionsHtml = "";
        for (const optionType of optionTypes) {
          const optionName = optionType.name;

          // Step 1: Check if option is generally allowed (applies to all options)
          const isGenerallyAllowed = !step.allowed_option_selectors ||
            step.allowed_option_selectors.includes(optionName);

          if (!isGenerallyAllowed) continue;

          // Step 2: Check if this option should be shown based on allowed_single_option_selectors
          // Only show options that are explicitly in the allowed_single_option_selectors array
          const shouldShow =
            step.allowed_single_option_selectors &&
            step.allowed_single_option_selectors.includes(optionName);

          if (!shouldShow) continue;

          const optionHtml = this.renderReviewOptionGroup(
            product,
            option,
            optionType,
            variant,
            reviewStepId
          );

          if (optionHtml) {
            variantOptionsHtml += optionHtml;
          }
        }

        return `
          <div class="cyo-review-step" data-step-id="${step.id}">
            ${productSwatchHtml}
            ${variantOptionsHtml}
          </div>
        `;
      } catch (error) {
        console.error("❌ CYO: Error rendering step summary:", error);
        return "";
      }
    };

    /**
     * Render a visual option group for review step (non-interactive)
     */
    renderReviewOptionGroup = (
      product,
      selectionOption,
      optionType,
      selectedVariant,
      reviewStepId
    ) => {
      try {
        const optionKey = optionType.key;
        const optionName = optionType.name;
        const optionIndex = optionType.index;
        const renderType = optionType.type;

        // Get the selected value for this option type
        let selectedValue = null;

        if (
          optionType.specialKey === "color" &&
          product.has_color_option &&
          product.color_option_index !== undefined
        ) {
          selectedValue = selectedVariant.color;
        } else if (
          optionType.specialKey === "size" &&
          product.has_size_option &&
          product.size_option_index !== undefined
        ) {
          selectedValue = selectedVariant.options[product.size_option_index];
        } else {
          selectedValue = selectedVariant.options[optionIndex];
        }

        if (!selectedValue) return "";

        const fieldsetId = `${reviewStepId}-${optionKey}`;
        const inputId = `${fieldsetId}-${selectedValue
          .toLowerCase()
          .replace(/[^a-z0-9]/g, "-")}`;

        if (renderType === "swatch" && optionKey === "color") {
          // Render color swatch (non-interactive)
          const valueHandle = selectedValue
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "-");

          return `
            <div class="cyo-variant-option">
              <label class="cyo-variant-option__label">${optionName} - <span class="cyo-step__selection-name">${selectedValue.toLowerCase()}</span></label>
              <fieldset class="cyo-variant-option__fieldset">
                <legend class="visually-hidden">${optionName} selection</legend>
                <div class="cyo-color-swatches">
                  <div class="cyo-color-option cyo-color-option--selected">
                    <input
                      type="radio"
                      class="cyo-color-option__input"
                      id="${inputId}"
                      name="${fieldsetId}"
                      value="${selectedValue}"
                      checked
                      disabled
                    />
                    <label for="${inputId}" class="cyo-color-option__label">
                      <div class="cyo-color-option__swatch" style="background: var(--swatch-${valueHandle});"></div>
                      <span class="visually-hidden">${selectedValue}</span>
                    </label>
                  </div>
                </div>
              </fieldset>
            </div>
          `;
        } else {
          // Render button-style options (size and other option types) - non-interactive
          const optionClass =
            optionKey === "size"
              ? "cyo-size-options"
              : `cyo-${optionKey}-options`;
          const itemClass =
            optionKey === "size"
              ? "cyo-size-option"
              : `cyo-${optionKey}-option`;

          return `
            <div class="cyo-variant-option">
              <label class="cyo-variant-option__label">${optionName} - <span class="cyo-step__selection-name">${selectedValue.toLowerCase()}</span></label>
              <fieldset class="cyo-variant-option__fieldset">
                <legend class="visually-hidden">${optionName} selection</legend>
                <div class="${optionClass}">
                  <div class="${itemClass} ${itemClass}--selected">
                    <input
                      type="radio"
                      class="${itemClass}__input"
                      id="${inputId}"
                      name="${fieldsetId}"
                      value="${selectedValue}"
                      checked
                      disabled
                    />
                    <label for="${inputId}" class="${itemClass}__label">${selectedValue}</label>
                  </div>
                </div>
              </fieldset>
            </div>
          `;
        }
      } catch (error) {
        console.error(
          `❌ CYO: Error rendering review option group for ${optionType.key}:`,
          error
        );
        return "";
      }
    };

    /**
     * Render unavailable message
     */
    renderUnavailableMessage = () => {
      return `
        <div class="cyo-error">
          <h3>No Available Options</h3>
          <p>Sorry, there are no available options for this step at the moment.</p>
        </div>
      `;
    };

    /**
     * Attach event listeners to shadow DOM elements using event delegation
     */
    attachEventListeners = () => {
      try {
        // Remove any existing listeners to avoid duplicates
        this.shadowRoot.removeEventListener(
          "change",
          this.handleDelegatedChange
        );
        this.shadowRoot.removeEventListener("click", this.handleDelegatedClick);
        this.shadowRoot.removeEventListener(
          "keydown",
          this.handleKeyboardNavigation
        );
        this.shadowRoot.removeEventListener("focusin", this.handleFocusIn);
        this.shadowRoot.removeEventListener("mousedown", this.handleMouseDown);
        this.shadowRoot.removeEventListener("pointerover", this.handlePointerOver);

        // Use event delegation for all change events
        this.shadowRoot.addEventListener("change", this.handleDelegatedChange);

        // Use event delegation for button clicks
        this.shadowRoot.addEventListener("click", this.handleDelegatedClick);

        // Add keyboard navigation support
        this.shadowRoot.addEventListener(
          "keydown",
          this.handleKeyboardNavigation
        );

        // Handle focus/mouse events for keyboard mode + aggregate prefetching.
        this.shadowRoot.addEventListener("focusin", this.handleFocusIn);
        this.shadowRoot.addEventListener("mousedown", this.handleMouseDown);
        this.shadowRoot.addEventListener("pointerover", this.handlePointerOver);

        if (this.allowDebug()) {
          console.log(
            "🎧 CYO: Event delegation listeners attached to shadow root (with accessibility enhancements)"
          );
        }
      } catch (error) {
        console.error("❌ CYO: Error attaching event listeners:", error);
      }
    };

    /**
     * Handle focusin events to track keyboard navigation and prefetch candidate aggregate media.
     * @param {FocusEvent} event
     */
    handleFocusIn = (event) => {
      this.keyboardNavigation = true;
      document.documentElement.setAttribute("data-keyboard-navigation", "true");
      const radioInput = event.target?.closest?.("input[type='radio']");
      if (radioInput) {
        this.scheduleAggregatePrefetch(radioInput);
      }
    };

    /**
     * Handle mousedown events to disable keyboard navigation mode.
     */
    handleMouseDown = () => {
      this.keyboardNavigation = false;
      document.documentElement.removeAttribute("data-keyboard-navigation");
    };

    /**
     * Handle hover intent events to prefetch likely aggregate media.
     * @param {MouseEvent} event
     */
    handlePointerOver = (event) => {
      const optionContainer = event.target?.closest?.(
        ".cyo-product-option, .cyo-size-option, .cyo-color-option"
      );

      // Only react when entering a new option container, not while moving inside it.
      if (
        optionContainer &&
        event.relatedTarget &&
        optionContainer.contains(event.relatedTarget)
      ) {
        return;
      }

      const radioInput =
        optionContainer?.querySelector("input[type='radio']") ||
        event.target?.closest?.("input[type='radio']");
      if (!radioInput || radioInput.disabled) return;

      const isRelevantSelection =
        radioInput.name?.includes("product-options") ||
        radioInput.name?.includes("-selection-");
      if (!isRelevantSelection) return;

      this.scheduleAggregatePrefetch(radioInput);
    };

    /**
     * Debounce prefetch attempts for hovered/focused selection inputs.
     * @param {HTMLInputElement} input
     */
    scheduleAggregatePrefetch = (input) => {
      if (!input?.name) return;
      const debounceKey = `aggregateMediaPrefetch:${input.name}:${input.value}:${input.dataset.productId || ""}:${input.dataset.optionId || ""}`;

      this.debounceManager.debounce(
        debounceKey,
        () => this.prefetchAggregateMediaForInput(input),
        this.aggregatePrefetchDebounceDelay
      );
    };

    /**
     * Predict the variant associated with a hovered/focused input and prefetch aggregate media.
     * @param {HTMLInputElement} input
     */
    prefetchAggregateMediaForInput = (input) => {
      try {
        const currentStep = this.getCurrentStep();
        if (!currentStep) return;

        const predictedVariant = this.getPredictedVariantFromInput(input, currentStep);
        if (!predictedVariant?.sku) return;

        const selectedVariants = [];
        for (let stepIndex = 0; stepIndex <= this.currentStepIndex; stepIndex++) {
          const step = this.config.steps[stepIndex];
          if (!step) continue;

          const selection = this.getSelectionForStep(step);
          const variant = step.id === currentStep.id
            ? predictedVariant
            : selection?.variant;

          if (variant?.sku) {
            selectedVariants.push(variant);
          }
        }

        const aggregateGallery = this.findAggregateMediaMatchForVariants(selectedVariants);
        const aggregateMediaUrl = aggregateGallery?.media_json_url;
        if (!aggregateMediaUrl || this.prefetchedAggregateMediaUrls.has(aggregateMediaUrl)) {
          return;
        }

        const productMediaComponent = this.productOverview?.querySelector("product-media");
        if (!productMediaComponent || typeof productMediaComponent.prefetchMedia !== "function") {
          return;
        }

        this.prefetchedAggregateMediaUrls.add(aggregateMediaUrl);
        productMediaComponent.prefetchMedia(aggregateMediaUrl);

        if (this.allowDebug()) {
          console.log(`🖼️ CYO: Prefetching aggregate media on intent: ${aggregateMediaUrl}`);
        }
      } catch (error) {
        console.error("❌ CYO: Error prefetching aggregate media on intent:", error);
      }
    };

    /**
     * Predict the variant that would be selected for an input.
     * Supports product option radios and variant option radios.
     * @param {HTMLInputElement} input
     * @param {Object} currentStep
     * @returns {Object|null}
     */
    getPredictedVariantFromInput = (input, currentStep) => {
      try {
        if (!input?.name || !currentStep) return null;

        // Product option radio: predicts using smart matching first, then first available variant.
        if (input.name.includes("product-options")) {
          const productId = parseInt(input.dataset.productId, 10);
          const optionId = parseInt(input.dataset.optionId, 10);
          const zoneType = input.dataset.zoneType || "regular";
          const isEligible = input.dataset.isEligible === "true";
          if (!isEligible) return null;

          const sourceOptions =
            zoneType === "members_only" ? currentStep.members_only_options : currentStep.options;
          const option = sourceOptions?.find((opt) => opt.id === optionId);
          const product = option?.products?.find((prod) => prod.id === productId);
          if (!product || !option) return null;

          const previousSelections = this.getPreviousVariantSelections();
          if (Object.keys(previousSelections).some((key) => previousSelections[key])) {
            const matchingVariant = this.findMatchingVariant(product, option, previousSelections);
            if (matchingVariant) return matchingVariant;
          }

          return this.getAvailableVariants(product, option)[0] || null;
        }

        // Variant option radio: predicts by applying this input's value to current form state.
        if (input.name.includes("-selection-")) {
          const selection = this.getSelectionForStep(currentStep);
          if (!selection?.product || !selection?.option) return null;

          const product = selection.product;
          const availableVariants = this.getAvailableVariants(product, selection.option);
          if (!availableVariants.length) return null;

          const optionTypes = this.getProductOptionTypes(product);
          const selectedValues = {};

          optionTypes.forEach((optionType) => {
            const selectedInput = this.shadowRoot.querySelector(
              `input[name*="${optionType.key}-selection"]:checked`
            );
            if (selectedInput) {
              selectedValues[optionType.key] = selectedInput.value;
            }
          });

          const hoveredOptionKey = input.name.split("-selection-")[0];
          selectedValues[hoveredOptionKey] = input.value;

          return availableVariants.find((variant) =>
            optionTypes.every((optionType) => {
              const selectedValue = selectedValues[optionType.key];
              if (!selectedValue) return true;

              if (optionType.key === "color") {
                return variant.color === selectedValue;
              }

              return variant.options[optionType.index] === selectedValue;
            })
          ) || null;
        }

        return null;
      } catch (error) {
        console.error("❌ CYO: Error predicting variant from input:", error);
        return null;
      }
    };

    /**
     * Handle delegated change events
     */
    handleDelegatedChange = (event) => {
      try {
        const target = event.target;

        if (this.allowDebug()) {
          console.log(
            `🎧 CYO: Delegated change event on:`,
            target,
            `checked: ${target.checked}`
          );
        }

        if (!target.matches("input[type='radio']") || !target.checked) {
          if (this.allowDebug()) {
            console.log(
              `🎧 CYO: Change event ignored - not a checked radio input`
            );
          }
          return;
        }

        if (this.allowDebug()) {
          console.log(
            `🎧 CYO: Processing radio change - ${target.name} = ${target.value}`
          );
        }

        // Product option selection
        if (target.name && target.name.includes("product-options")) {
          const productId = parseInt(target.dataset.productId, 10);
          const optionId = parseInt(target.dataset.optionId, 10);
          const zoneType = target.dataset.zoneType || 'regular';
          const isEligible = target.dataset.isEligible === 'true';

          // JavaScript protection: prevent selection of ineligible options
          if (!isEligible) {
            if (this.allowDebug()) {
              console.log(
                `🚫 CYO: Product selection blocked - Customer not eligible for Product: ${productId}, Option: ${optionId}, Zone: ${zoneType}`
              );
            }

            // Uncheck the input and prevent further processing
            target.checked = false;

            // Announce to screen readers
            this.announceToScreenReader(
              "This selection requires membership access. Please explore memberships to unlock.",
              "assertive"
            );

            return;
          }

          if (this.allowDebug()) {
            console.log(
              `🎧 CYO: Product selection detected - Product: ${productId}, Option: ${optionId}, Zone: ${zoneType}`
            );
          }

          this.handleProductSelection(productId, optionId, zoneType);
          return;
        }

        // Variant option selection (color, size, etc.)
        if (target.name && target.name.includes("-selection-")) {
          const currentStep = this.getCurrentStep();
          const selection = this.getSelectionForStep(currentStep);

          if (selection && selection.product) {
            const availableVariants = this.getAvailableVariants(
              selection.product,
              selection.option
            );

            if (this.allowDebug()) {
              console.log(
                `🎧 CYO: Variant option selection detected - ${target.name} = ${target.value}`
              );
            }

            this.updateVariantSelection(
              selection.product,
              selection,
              availableVariants
            );
          }
          return;
        }

        if (this.allowDebug()) {
          console.log(`🎧 CYO: Unhandled change event for: ${target.name}`);
        }
      } catch (error) {
        console.error("❌ CYO: Error in delegated change handler:", error);
      }
    };

    /**
     * Handle delegated click events with debouncing
     */
    handleDelegatedClick = (event) => {
      try {
        if (this.allowDebug()) {
          console.log(
            "🎧 CYO: Delegated click event",
            event.target,
            event.currentTarget
          );
        }

        // Check if we're in a debounce period
        if (this.debounceManager.timers.has("clickDebounce")) {
          if (this.allowDebug()) {
            console.log("🎧 CYO: Click ignored - debounce active");
          }
          return;
        }

        const target = event.target;

        // Handle color swatch clicks (on the swatch div or color option wrapper)
        if (
          target.matches(".cyo-color-option__swatch") ||
          target.matches(".cyo-color-option") ||
          target.closest(".cyo-color-option")
        ) {
          const colorOption = target.closest(".cyo-color-option");
          if (colorOption) {
            const radioInput = colorOption.querySelector(
              ".cyo-color-option__input"
            );
            if (radioInput && !radioInput.checked) {
              if (this.allowDebug()) {
                console.log(
                  "🎧 CYO: Color swatch clicked - triggering radio input",
                  radioInput
                );
              }
              this.startClickDebounce();
              radioInput.checked = true;
              radioInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
            return;
          }
        }

        // Handle size option clicks
        if (
          target.matches(".cyo-size-option") ||
          target.closest(".cyo-size-option")
        ) {
          const sizeOption = target.closest(".cyo-size-option");
          if (sizeOption) {
            const radioInput = sizeOption.querySelector(
              ".cyo-size-option__input"
            );
            if (radioInput && !radioInput.checked) {
              if (this.allowDebug()) {
                console.log(
                  "🎧 CYO: Size option clicked - triggering radio input",
                  radioInput
                );
              }
              this.startClickDebounce();
              radioInput.checked = true;
              radioInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
            return;
          }
        }

        // Handle product option clicks
        if (
          target.matches(".cyo-product-option") ||
          target.closest(".cyo-product-option")
        ) {
          const productOption = target.closest(".cyo-product-option");
          if (productOption) {
            const radioInput = productOption.querySelector(
              ".cyo-product-option__input"
            );
            if (radioInput && !radioInput.checked) {
              // Check if this option is eligible before allowing interaction
              const isEligible = radioInput.dataset.isEligible === 'true';

              if (!isEligible) {
                if (this.allowDebug()) {
                  console.log(
                    "🚫 CYO: Product option click blocked - customer not eligible",
                    radioInput
                  );
                }

                // Announce to screen readers
                this.announceToScreenReader(
                  "This selection requires membership access. Please explore memberships to unlock.",
                  "assertive"
                );

                return;
              }

              if (this.allowDebug()) {
                console.log(
                  "🎧 CYO: Product option clicked - triggering radio input",
                  radioInput
                );
              }
              this.startClickDebounce();
              radioInput.checked = true;
              radioInput.dispatchEvent(new Event("change", { bubbles: true }));
            }
            return;
          }
        }

        // Handle MORE OPTIONS + button clicks (only with --show-more modifier)
        if (
          target.matches(".cyo-more-options-btn--show-more") ||
          target.closest(".cyo-more-options-btn--show-more")
        ) {
          event.preventDefault();
          const button = target.matches(".cyo-more-options-btn--show-more") ? target : target.closest(".cyo-more-options-btn--show-more");
          const zoneKey = button?.dataset.zoneKey;
          const zoneType = button?.dataset.zoneType;

          if (this.allowDebug()) {
            console.log(`🎧 CYO: MORE OPTIONS + button clicked for zone: ${zoneKey}`);
          }
          this.startClickDebounce();
          this.handleMoreOptionsClick(zoneKey, zoneType);
          return;
        }

        // Handle LESS OPTIONS - button clicks (using --show-less modifier)
        if (
          target.matches(".cyo-more-options-btn--show-less") ||
          target.closest(".cyo-more-options-btn--show-less")
        ) {
          event.preventDefault();
          const button = target.matches(".cyo-more-options-btn--show-less") ? target : target.closest(".cyo-more-options-btn--show-less");
          const zoneKey = button?.dataset.zoneKey;
          const zoneType = button?.dataset.zoneType;

          if (this.allowDebug()) {
            console.log(`🎧 CYO: LESS OPTIONS - button clicked for zone: ${zoneKey}`);
          }
          this.startClickDebounce();
          this.handleLessOptionsClick(zoneKey, zoneType);
          return;
        }

        // Back button (both footer and header) - improved targeting
        const backButton = target.closest(
          ".cyo-step__action--back, .cyo-step__nav-item--back"
        );
        if (backButton) {
          event.preventDefault();
          if (this.allowDebug()) {
            console.log("🎧 CYO: Back button clicked", backButton);
          }

          // Add loading state to back button
          backButton.classList.add("loading");
          const spinner = backButton.querySelector(".loading-overlay__spinner");
          if (spinner) {
            spinner.classList.remove("hidden");
          }

          this.startClickDebounce();
          this.handleBack();
          return;
        }

        // Add to Bag button - dedicated handling
        const addToBagButton = target.closest(".cyo-step__action--add-to-bag");
        if (addToBagButton) {
          event.preventDefault();
          if (this.allowDebug()) {
            console.log("🎧 CYO: Add to Bag button clicked", addToBagButton);
          }

          // Add immediate loading state to add to bag button
          addToBagButton.classList.add("loading");
          const spinner = addToBagButton.querySelector(
            ".loading-overlay__spinner"
          );
          if (spinner) {
            spinner.classList.remove("hidden");
          }

          this.startClickDebounce();
          this.handleAddToBag();
          return;
        }

        // Next/Review button (both footer and header) - improved targeting
        const nextButton = target.closest(
          ".cyo-step__action--next, .cyo-step__nav-item--next"
        );
        if (nextButton) {
          event.preventDefault();
          if (this.allowDebug()) {
            console.log("🎧 CYO: Next button clicked", nextButton);
          }

          // Add loading state to next button
          nextButton.classList.add("loading");
          const spinner = nextButton.querySelector(".loading-overlay__spinner");
          if (spinner) {
            spinner.classList.remove("hidden");
          }

          this.startClickDebounce();
          this.handleNext();
          return;
        }

        if (this.allowDebug()) {
          console.log("🎧 CYO: No matching click handler found for", target);
        }
      } catch (error) {
        console.error("❌ CYO: Error in delegated click handler:", error);
      }
    };

    /**
     * Start click debounce timer to prevent rapid successive clicks
     */
    startClickDebounce = () => {
      this.debounceManager.debounce(
        "clickDebounce",
        () => {
          if (this.allowDebug()) {
            console.log("🎧 CYO: Click debounce cleared");
          }
        },
        this.clickDebounceDelay
      );

      if (this.allowDebug()) {
        console.log(
          `🎧 CYO: Click debounce started (${this.clickDebounceDelay}ms)`
        );
      }
    };

    /**
     * Handle MORE OPTIONS + button click - expand to show all products for specific zone
     */
    handleMoreOptionsClick = async (zoneKey, zoneType) => {
      try {
        if (this.allowDebug()) {
          console.log(`📖 CYO: Expanding product options for zone: ${zoneKey}`);
        }

        // Add loading state to button to prevent multiple clicks
        const moreButton = this.shadowRoot.querySelector(
          `.cyo-more-options-btn--show-more[data-zone-key="${zoneKey}"]`
        );
        if (moreButton) {
          moreButton.classList.add("loading");
          moreButton.disabled = true;
        }

        // Set zone-specific expansion state
        this.zoneExpansionState[zoneKey] = true;
        await this.reRenderZoneProductOptions(zoneKey, zoneType);

        // Announce to screen readers
        this.announceToScreenReader(`Expanded ${zoneType} options to show all`, "polite");
      } catch (error) {
        console.error(`❌ CYO: Error handling MORE OPTIONS click for zone ${zoneKey}:`, error);
      }
    };

    /**
     * Handle LESS OPTIONS - button click - collapse to show first 8 products for specific zone
     */
    handleLessOptionsClick = async (zoneKey, zoneType) => {
      try {
        if (this.allowDebug()) {
          console.log(`📖 CYO: Collapsing product options for zone: ${zoneKey}`);
        }

        // Add loading state to button to prevent multiple clicks
        const lessButton = this.shadowRoot.querySelector(
          `.cyo-more-options-btn:not(.cyo-more-options-btn--show-more)[data-zone-key="${zoneKey}"]`
        );
        if (lessButton) {
          lessButton.classList.add("loading");
          lessButton.disabled = true;
        }

        // Set zone-specific expansion state
        this.zoneExpansionState[zoneKey] = false;
        await this.reRenderZoneProductOptions(zoneKey, zoneType);

        // Announce to screen readers
        this.announceToScreenReader(
          `Collapsed ${zoneType} options to show fewer`,
          "polite"
        );
      } catch (error) {
        console.error(`❌ CYO: Error handling LESS OPTIONS click for zone ${zoneKey}:`, error);
      }
    };

    /**
     * Smoothly expand or collapse product options for a specific zone without losing selection state
     */
    reRenderZoneProductOptions = async (zoneKey, zoneType) => {
      try {
        const currentStep = this.getCurrentStep();
        const selectionZones = this.getAvailableOptions(currentStep);
        const zone = selectionZones[zoneType];

        if (!zone || !zone.options.length) {
          if (this.allowDebug()) {
            console.warn(`⚠️ CYO: No zone data found for ${zoneKey}`);
          }
          return;
        }

        // Get step-specific pagination settings
        const paginationSettings = this.getStepPaginationSettings(currentStep);

        // Find the specific zone's fieldset and options grid
        const selectionZoneElement = this.shadowRoot.querySelector(
          `.cyo-selection-zone[data-zone-type="${zoneType}"]`
        );

        if (!selectionZoneElement) {
          if (this.allowDebug()) {
            console.warn(`⚠️ CYO: Zone element not found for ${zoneType}`);
          }
          return;
        }

        const fieldset = selectionZoneElement.querySelector(".cyo-variant-option__fieldset");
        const optionsGrid = selectionZoneElement.querySelector(".cyo-product-options");

        if (!fieldset || !optionsGrid) {
          if (this.allowDebug()) {
            console.warn(`⚠️ CYO: Required elements not found in zone ${zoneType}`);
          }
          return;
        }

        if (this.allowDebug()) {
          console.log(
            `🎬 CYO: Starting animated transition for zone ${zoneKey} to ${
              this.zoneExpansionState[zoneKey] ? "expanded" : "collapsed"
            } state`
          );
        }

        // Get all products from zone options
        const allProducts = [];
        zone.options.forEach((option) => {
          const products = option.products.filter(
            (product) =>
              this.isProductAvailable(product) &&
              this.getAvailableVariants(product, option).length > 0
          );
          products.forEach((product) => allProducts.push({ product, option }));
        });

        const totalProducts = allProducts.length;
        const isExpanding = this.zoneExpansionState[zoneKey];

        // Measure current height before changes
        const currentHeight = fieldset.offsetHeight;

        // Temporarily set explicit height to enable smooth transition
        fieldset.style.height = `${currentHeight}px`;

        if (isExpanding) {
          // EXPANDING: Add the additional product options
          const currentOptions = optionsGrid.querySelectorAll(".cyo-product-option");
          const currentCount = currentOptions.length;
          const additionalProducts = allProducts.slice(currentCount);

          if (additionalProducts.length > 0) {
            // Create HTML for additional products
            const additionalHTML = additionalProducts
              .map(({ product, option }) => {
                const inputId = `product-options-${currentStep.id}-${zoneType}-product-${product.id}-option-${option.id}`;
                const radioName = `product-options-${currentStep.id}-${zoneType}-selection`;

                // Check if this option is eligible (for members-only zones)
                const isEligible = zoneType === 'members_only' ? (option.isEligible !== false) : true;
                const isDisabled = !isEligible;

                // Add classes for disabled state
                const optionClasses = `cyo-product-option${isDisabled ? ' cyo-product-option--disabled' : ''}`;
                const inputClasses = `cyo-product-option__input${isDisabled ? ' cyo-product-option__input--disabled' : ''}`;

                return `
                <div class="${optionClasses}" style="opacity: 0; transform: translateY(20px);">
                  <input
                    type="radio"
                    id="${inputId}"
                    class="${inputClasses}"
                    name="${radioName}"
                    value="${product.id}-${option.id}"
                    data-product-id="${product.id}"
                    data-option-id="${option.id}"
                    data-zone-type="${zoneType}"
                    data-is-eligible="${isEligible}"
                    ${isDisabled ? "disabled" : ""}
                  />
                  <label for="${inputId}" class="cyo-product-option__label">
                    ${product.media[0] || ""}
                    <div class="cyo-product-option__title">${product.title}</div>
                  </label>
                </div>
              `;
              })
              .join("");

            // Add the new products to the grid
            optionsGrid.insertAdjacentHTML("beforeend", additionalHTML);
          }
        } else {
            // COLLAPSING: Remove extra product options
            const allOptions = optionsGrid.querySelectorAll(".cyo-product-option");
            const keepCount = Math.min(paginationSettings.optionsPerPage, totalProducts);
            const optionsToRemove = Array.from(allOptions).slice(keepCount);

          // Fade out options that will be removed
          optionsToRemove.forEach((option, index) => {
            setTimeout(() => {
              option.style.transition = "opacity 0.3s ease, transform 0.3s ease";
              option.style.opacity = "0";
              option.style.transform = "translateY(-20px)";
            }, index * 30); // 30ms stagger for removal
          });
        }

        // Measure new height after content changes
        fieldset.style.height = "auto";
        const newHeight = fieldset.offsetHeight;
        fieldset.style.height = `${currentHeight}px`;

        // Animate to new height
        requestAnimationFrame(() => {
          fieldset.style.height = `${newHeight}px`;

          // Update button during both expansion and collapse
          setTimeout(() => {
            this.updateZoneToggleButton(zoneKey, zoneType, totalProducts);
          }, 100);

          if (isExpanding) {
            // Animate in new options with stagger
            const newOptions = optionsGrid.querySelectorAll(".cyo-product-option");
            const startIndex = Math.min(paginationSettings.optionsPerPage, newOptions.length);

            newOptions.forEach((option, index) => {
              if (index >= startIndex) {
                setTimeout(() => {
                  option.style.transition = "opacity 0.3s ease, transform 0.3s ease";
                  option.style.opacity = "1";
                  option.style.transform = "translateY(0)";
                }, (index - startIndex) * 50); // 50ms stagger for new items
              }
            });
          }

          // Clean up after animation
          setTimeout(() => {
            fieldset.style.height = "auto";

            if (!isExpanding) {
              // Remove the hidden options from DOM
              const keepCount = Math.min(paginationSettings.optionsPerPage, totalProducts);
              const optionsToRemove = Array.from(
                optionsGrid.querySelectorAll(".cyo-product-option")
              ).slice(keepCount);
              optionsToRemove.forEach((option) => option.remove());
            }

            // Clean up inline styles
            const remainingOptions = optionsGrid.querySelectorAll(".cyo-product-option");
            remainingOptions.forEach((option) => {
              option.style.opacity = "";
              option.style.transform = "";
              option.style.transition = "";
            });

            if (this.allowDebug()) {
              console.log(`✅ CYO: Animated transition completed for zone ${zoneKey}`);
            }
          }, 400); // Match transition duration
        });
      } catch (error) {
        console.error(`❌ CYO: Error in zone-specific product options transition:`, error);
      }
    };

    /**
     * Smoothly expand or collapse product options without losing selection state
     * @deprecated - Use reRenderZoneProductOptions for zone-specific rendering
     */
    reRenderProductOptions = async () => {
      try {
        const currentStep = this.getCurrentStep();
        const availableOptions = this.getAvailableOptions(currentStep);
        const fieldset = this.shadowRoot.querySelector(
          ".cyo-variant-option__fieldset"
        );
        const optionsGrid = this.shadowRoot.querySelector(
          ".cyo-product-options"
        );

        if (!fieldset || !optionsGrid || Object.keys(availableOptions).length === 0) {
          return;
        }

        // Get step-specific pagination settings
        const paginationSettings = this.getStepPaginationSettings(currentStep);

        if (this.allowDebug()) {
          console.log(
            `🎬 CYO: Starting animated transition to ${
              this.productOptionsExpanded ? "expanded" : "collapsed"
            } state`
          );
        }

        // Get all products from all options (handle new zone structure)
        const allProducts = [];
        if (availableOptions.regular) {
          availableOptions.regular.options.forEach((option) => {
            const products = option.products.filter(
              (product) =>
                this.isProductAvailable(product) &&
                this.getAvailableVariants(product, option).length > 0
            );
            products.forEach((product) => allProducts.push({ product, option }));
          });
        }

        const totalProducts = allProducts.length;
        const isExpanding = this.productOptionsExpanded;

        // Measure current height before changes
        const currentHeight = fieldset.offsetHeight;

        // Temporarily set explicit height to enable smooth transition
        fieldset.style.height = `${currentHeight}px`;

        if (isExpanding) {
          // EXPANDING: Add the additional product options
          const currentOptions = optionsGrid.querySelectorAll(
            ".cyo-product-option"
          );
          const currentCount = currentOptions.length;
          const additionalProducts = allProducts.slice(currentCount);

          if (additionalProducts.length > 0) {
            // Create HTML for additional products
            const additionalHTML = additionalProducts
              .map(({ product, option }) => {
                const inputId = `product-options-${currentStep.id}-product-${product.id}-option-${option.id}`;
                const radioName = `product-options-${currentStep.id}-selection`;

                // Check if this option is eligible (this is deprecated method, assume regular zone)
                const isEligible = true;
                const isDisabled = !isEligible;

                // Add classes for disabled state
                const optionClasses = `cyo-product-option${isDisabled ? ' cyo-product-option--disabled' : ''}`;
                const inputClasses = `cyo-product-option__input${isDisabled ? ' cyo-product-option__input--disabled' : ''}`;

                return `
                <div class="${optionClasses}" style="opacity: 0; transform: translateY(20px);">
                  <input
                    type="radio"
                    id="${inputId}"
                    class="${inputClasses}"
                    name="${radioName}"
                    value="${product.id}-${option.id}"
                    data-product-id="${product.id}"
                    data-option-id="${option.id}"
                    data-is-eligible="${isEligible}"
                    ${isDisabled ? "disabled" : ""}
                  />
                  <label for="${inputId}" class="cyo-product-option__label">
                    ${product.media[0] || ""}
                    <div class="cyo-product-option__title">${
                      product.title
                    }</div>
                  </label>
                </div>
              `;
              })
              .join("");

            // Add the new products to the grid
            optionsGrid.insertAdjacentHTML("beforeend", additionalHTML);
          }
        } else {
          // COLLAPSING: Remove extra product options
          const allOptions = optionsGrid.querySelectorAll(
            ".cyo-product-option"
          );

          // Check if pagination should be available based on step configuration
          const shouldShowPagination =
            paginationSettings.enabled &&
            totalProducts > paginationSettings.optionsPerPage;

          const keepCount = shouldShowPagination
            ? paginationSettings.optionsPerPage
            : totalProducts;
          const optionsToRemove = Array.from(allOptions).slice(keepCount);

          // Fade out options that will be removed
          optionsToRemove.forEach((option, index) => {
            setTimeout(() => {
              option.style.transition =
                "opacity 0.3s ease, transform 0.3s ease";
              option.style.opacity = "0";
              option.style.transform = "translateY(-20px)";
            }, index * 30); // 30ms stagger for removal
          });
        }

        // Measure new height after content changes
        fieldset.style.height = "auto";
        const newHeight = fieldset.offsetHeight;
        fieldset.style.height = `${currentHeight}px`;

        // Animate to new height
        requestAnimationFrame(() => {
          fieldset.style.height = `${newHeight}px`;

          // Update button during both expansion and collapse (during height animation)
          setTimeout(() => {
            this.updateToggleButton(totalProducts);
          }, 100); // Start button transition 100ms into the height animation

          if (isExpanding) {
            // Animate in new options with stagger
            const newOptions = optionsGrid.querySelectorAll(
              ".cyo-product-option"
            );

            // Check if pagination should be available based on step configuration
            const shouldShowPagination =
              paginationSettings.enabled &&
              totalProducts > paginationSettings.optionsPerPage;

            const startIndex = shouldShowPagination
              ? Math.min(
                  paginationSettings.optionsPerPage,
                  newOptions.length
                )
              : newOptions.length;

            newOptions.forEach((option, index) => {
              if (index >= startIndex) {
                setTimeout(() => {
                  option.style.transition =
                    "opacity 0.3s ease, transform 0.3s ease";
                  option.style.opacity = "1";
                  option.style.transform = "translateY(0)";
                }, (index - startIndex) * 50); // 50ms stagger for new items
              }
            });
          }

          // Clean up after animation
          setTimeout(() => {
            fieldset.style.height = "auto";

            if (!isExpanding) {
              // Remove the hidden options from DOM

              // Check if pagination should be available based on step configuration
              const shouldShowPagination =
                paginationSettings.enabled &&
                totalProducts > paginationSettings.optionsPerPage;

              const keepCount = shouldShowPagination
                ? paginationSettings.optionsPerPage
                : totalProducts;
              const optionsToRemove = Array.from(
                optionsGrid.querySelectorAll(".cyo-product-option")
              ).slice(keepCount);
              optionsToRemove.forEach((option) => option.remove());
            }

            // Clean up inline styles
            const remainingOptions = optionsGrid.querySelectorAll(
              ".cyo-product-option"
            );
            remainingOptions.forEach((option) => {
              option.style.opacity = "";
              option.style.transform = "";
              option.style.transition = "";
            });

            if (this.allowDebug()) {
              console.log(
                "✅ CYO: Animated product options transition completed"
              );
            }
          }, 400); // Match transition duration
        });
      } catch (error) {
        console.error(
          "❌ CYO: Error in animated product options transition:",
          error
        );
      }
    };

    /**
     * Update zone-specific toggle button with smooth animation
     */
    updateZoneToggleButton = (zoneKey, zoneType, totalProducts) => {
      try {
        const toggleContainer = this.shadowRoot.querySelector(
          `.cyo-product-options-toggle[data-zone-key="${zoneKey}"]`
        );
        if (!toggleContainer) return;

        // Get current step and its pagination settings
        const currentStep = this.getCurrentStep();
        const paginationSettings = this.getStepPaginationSettings(currentStep);

        // Check if pagination should be available based on step configuration
        const shouldShowPagination =
          paginationSettings.enabled &&
          totalProducts > paginationSettings.optionsPerPage;

        const hasMoreProducts =
          shouldShowPagination &&
          totalProducts > paginationSettings.optionsPerPage;

        // Add transition class for smooth fade
        toggleContainer.classList.add("transitioning");

        setTimeout(() => {
          if (hasMoreProducts) {
            const isExpanded = this.zoneExpansionState[zoneKey];
            const buttonHTML = isExpanded
              ? `<button type="button" class="cyo-more-options-btn cyo-more-options-btn--show-less" data-zone-key="${zoneKey}" data-zone-type="${zoneType}">
                <span>${paginationSettings.viewLessCta}</span>
              </button>`
              : `<button type="button" class="cyo-more-options-btn cyo-more-options-btn--show-more" data-zone-key="${zoneKey}" data-zone-type="${zoneType}">
                <span>${paginationSettings.viewMoreCta}</span>
              </button>`;

            toggleContainer.innerHTML = buttonHTML;
          } else {
            toggleContainer.innerHTML = "";
          }

          // Remove transition class after content change
          setTimeout(() => {
            toggleContainer.classList.remove("transitioning");
          }, 50); // Small delay to ensure content is updated
        }, 150); // Half the transition duration for smooth crossfade

        if (this.allowDebug()) {
          console.log(`✅ CYO: Updated toggle button for zone ${zoneKey}`);
        }
      } catch (error) {
        console.error(`❌ CYO: Error updating zone toggle button for ${zoneKey}:`, error);
      }
    };

    /**
     * Update just the toggle button with smooth animation
     * @deprecated - Use updateZoneToggleButton for zone-specific updates
     */
    updateToggleButton = (totalProducts) => {
      try {
        const toggleContainer = this.shadowRoot.querySelector(
          ".cyo-product-options-toggle"
        );
        if (!toggleContainer) return;

        // Get current step and its pagination settings
        const currentStep = this.getCurrentStep();
        const paginationSettings = this.getStepPaginationSettings(currentStep);

        // Check if pagination should be available based on step configuration
        const shouldShowPagination =
          paginationSettings.enabled &&
          totalProducts > paginationSettings.optionsPerPage;

        const hasMoreProducts =
          shouldShowPagination &&
          totalProducts > paginationSettings.optionsPerPage;

        // Add transition class for smooth fade
        toggleContainer.classList.add("transitioning");

        setTimeout(() => {
          if (hasMoreProducts) {
            const buttonHTML = this.productOptionsExpanded
              ? `<button type="button" class="cyo-more-options-btn cyo-more-options-btn--show-less">
                <span>${paginationSettings.viewLessCta}</span>
              </button>`
              : `<button type="button" class="cyo-more-options-btn cyo-more-options-btn--show-more">
                <span>${paginationSettings.viewMoreCta}</span>
              </button>`;

            toggleContainer.innerHTML = buttonHTML;
          } else {
            toggleContainer.innerHTML = "";
          }

          // Remove transition class after content change
          setTimeout(() => {
            toggleContainer.classList.remove("transitioning");
          }, 50); // Small delay to ensure content is updated
        }, 150); // Half the transition duration for smooth crossfade
      } catch (error) {
        console.error("❌ CYO: Error updating toggle button:", error);
      }
    };

    /**
     * Get previous variant selections for smart matching
     */
    getPreviousVariantSelections = () => {
      const previousSelections = {};

      for (const selection of this.selections.values()) {
        if (selection.variant && selection.product) {
          const optionTypes = this.getProductOptionTypes(selection.product);

          optionTypes.forEach((optionType) => {
            // For color with special properties, use variant.color
            if (
              optionType.specialKey === "color" &&
              selection.product.has_color_option &&
              selection.variant.color
            ) {
              previousSelections.color = selection.variant.color;
            }
            // For all other cases, use the options array
            else if (
              selection.variant.options &&
              optionType.index !== undefined
            ) {
              const optionValue = selection.variant.options[optionType.index];
              if (optionValue) {
                previousSelections[optionType.key] = optionValue;
              }
            }
          });
        }
      }

      return previousSelections;
    };

    /**
     * Find matching variant based on previous selections
     */
    findMatchingVariant = (product, option, previousSelections) => {
      const availableVariants = this.getAvailableVariants(product, option);
      const optionTypes = this.getProductOptionTypes(product);

      return availableVariants.find((variant) => {
        return optionTypes.every((optionType) => {
          const previousValue = previousSelections[optionType.key];
          if (!previousValue) return true; // No previous selection for this option type

          // For color with special properties, use variant.color
          if (optionType.specialKey === "color" && product.has_color_option) {
            return variant.color === previousValue;
          }
          // For all other cases, use the options array
          else {
            const optionIndex = optionType.index;
            if (
              optionIndex !== undefined &&
              variant.options &&
              variant.options[optionIndex]
            ) {
              return variant.options[optionIndex] === previousValue;
            }
          }

          return true; // If we can't check, don't filter on this option
        });
      });
    };

    /**
     * Update product swatch selection visual state
     */
    updateProductSwatchSelection = (product, option, zoneType = 'regular') => {
      try {
        const productSwatches = this.shadowRoot.querySelectorAll(
          ".cyo-product-option"
        );
        productSwatches.forEach((swatch) => {
          const input = swatch.querySelector(".cyo-product-option__input");
          if (input) {
            const inputProductId = parseInt(input.dataset.productId, 10);
            const inputOptionId = parseInt(input.dataset.optionId, 10);
            const inputZoneType = input.dataset.zoneType || 'regular';

            if (inputProductId === product.id && inputOptionId === option.id && inputZoneType === zoneType) {
              swatch.classList.add("cyo-product-option--selected");
              input.checked = true;
              if (this.allowDebug()) {
                console.log(
                  `✅ CYO: Updated product swatch selection for product ${product.id}, option ${option.id}, zone ${zoneType}`
                );
              }
            } else {
              swatch.classList.remove("cyo-product-option--selected");
              input.checked = false;
            }
          }
        });
      } catch (error) {
        console.error(
          "❌ CYO: Error updating product swatch selection:",
          error
        );
      }
    };

    /**
     * Restore step state when returning to a previously visited step
     * @param {Object} existingSelection - The existing selection to restore
     */
    restoreStepState = (existingSelection) => {
      try {
        if (this.allowDebug()) {
          console.log("🔄 CYO: Restoring step state", existingSelection);
        }

        // Restore product selection UI
        this.updateProductSwatchSelection(
          existingSelection.product,
          existingSelection.option,
          existingSelection.zoneType || 'regular'
        );

        // Render variant selection for the existing product
        const zoneKey = existingSelection.zoneKey || existingSelection.zoneType || 'regular';
        this.renderVariantSelection(
          existingSelection.product,
          existingSelection,
          zoneKey
        );

        // Update option value displays
        this.updateOptionValueDisplay();

        // Update action buttons to reflect current state
        this.updateActionButtons();

        // Update media gallery to reflect current state
        this.debouncedUpdateMediaGallery();

        if (this.allowDebug()) {
          console.log("✅ CYO: Step state restoration completed");
        }
      } catch (error) {
        console.error("❌ CYO: Error restoring step state:", error);
      }
    };

    /**
     * Update the option value display based on current selection
     * Now handles zone-specific selection names
     */
    updateOptionValueDisplay = () => {
      try {
        const currentStep = this.getCurrentStep();
        const selection = this.getSelectionForStep(currentStep);

        // Update zone-specific selection names
        const selectionZones = this.shadowRoot.querySelectorAll('.cyo-selection-zone');

        selectionZones.forEach((zoneElement) => {
          const zoneType = zoneElement.dataset.zoneType;
          const selectionNameElement = zoneElement.querySelector('.cyo-step__selection-name');

          if (selectionNameElement) {
            // Show selection name only if the selection is from this zone
            if (selection && selection.product && selection.zoneType === zoneType) {
              const decodedTitle = decodeHTMLEntities(selection.product.title);
              selectionNameElement.textContent = decodedTitle.toLowerCase();

              if (this.allowDebug()) {
                console.log(
                  `🏷️ CYO: Updated ${zoneType} zone selection name with product: ${selection.product.title}`
                );
              }
            } else {
              // Clear selection name for zones that don't have the current selection
              selectionNameElement.textContent = "";

              if (this.allowDebug() && selection) {
                console.log(
                  `🏷️ CYO: Cleared ${zoneType} zone selection name (selection is in ${selection.zoneType || 'unknown'} zone)`
                );
              }
            }
          }
        });

        // Update option values for all option types within the specific zone
        if (selection && selection.product && selection.zoneType) {
          const optionTypes = this.getProductOptionTypes(selection.product);
          const zoneElement = this.shadowRoot.querySelector(
            `.cyo-selection-zone[data-zone-type="${selection.zoneType}"]`
          );

          if (zoneElement) {
            optionTypes.forEach((optionType) => {
              const optionValueElement = zoneElement.querySelector(
                `[data-option-type='${optionType.key}']`
              );
              if (optionValueElement) {
                if (selection.variant) {
                  let optionText;

                  // For color with special properties, use variant.color
                  if (
                    optionType.specialKey === "color" &&
                    selection.product.has_color_option &&
                    selection.variant.color
                  ) {
                    const decodedColor =
                      decodeHTMLEntities(
                        selection.variant.color
                      );
                    optionText = decodedColor.toLowerCase();

                    // Check if variant has color_family for sub-value
                    if (selection.variant.color_family) {
                      const decodedColorFamily =
                        decodeHTMLEntities(
                          selection.variant.color_family
                        );
                      optionValueElement.replaceChildren();
                      optionValueElement.appendChild(
                        document.createTextNode(optionText)
                      );
                      const subValueElement =
                        createElementFromHTML(
                          `<span class="cyo-step__selection-detail">${decodedColorFamily.toLowerCase()}</span>`
                        );
                      if (subValueElement) {
                        optionValueElement.appendChild(
                          document.createTextNode(" ")
                        );
                        optionValueElement.appendChild(subValueElement);
                      }
                      if (this.allowDebug()) {
                        console.log(
                          `🎨 CYO: Updated ${optionType.key} option value: ${optionText} (${selection.variant.color_family})`
                        );
                      }
                    } else {
                      optionValueElement.textContent = optionText || "";
                      if (this.allowDebug() && optionText) {
                        console.log(
                          `🎨 CYO: Updated ${optionType.key} option value: ${optionText}`
                        );
                      }
                    }
                  } else {
                    // For all other option types, get the value from the options array
                    const optionIndex = optionType.index;
                    if (
                      optionIndex !== undefined &&
                      selection.variant.options &&
                      selection.variant.options[optionIndex]
                    ) {
                      const decodedOption =
                        decodeHTMLEntities(
                          selection.variant.options[optionIndex]
                        );
                      optionText = decodedOption.toLowerCase();
                      optionValueElement.textContent = optionText;
                      if (this.allowDebug()) {
                        console.log(
                          `📏 CYO: Updated ${optionType.key} option value: ${optionText}`
                        );
                      }
                    } else {
                      optionValueElement.textContent = "";
                    }
                  }
                } else if (selection.selectedValues && selection.selectedValues[optionType.key]) {
                  // For unavailable combinations, use the stored selected values
                  const selectedValue = selection.selectedValues[optionType.key];
                  const decodedValue = decodeHTMLEntities(selectedValue);

                  // For color options, try to find color family information
                  if (optionType.key === "color" && selection.product.has_color_option) {
                    // Find any variant with this color to get the color_family
                    const colorVariant = selection.product.variants.find(v =>
                      v.color && decodeHTMLEntities(v.color).toLowerCase() === decodedValue.toLowerCase()
                    );

                    if (colorVariant && colorVariant.color_family) {
                      const decodedColorFamily = decodeHTMLEntities(colorVariant.color_family);
                      optionValueElement.replaceChildren();
                      optionValueElement.appendChild(
                        document.createTextNode(decodedValue.toLowerCase())
                      );
                      const subValueElement = createElementFromHTML(
                        `<span class="cyo-step__selection-detail">${decodedColorFamily.toLowerCase()}</span>`
                      );
                      if (subValueElement) {
                        optionValueElement.appendChild(document.createTextNode(" "));
                        optionValueElement.appendChild(subValueElement);
                      }
                      if (this.allowDebug()) {
                        console.log(
                          `🎨 CYO: Updated unavailable ${optionType.key} option value: ${decodedValue.toLowerCase()} (${decodedColorFamily})`
                        );
                      }
                    } else {
                      optionValueElement.textContent = decodedValue.toLowerCase();
                      if (this.allowDebug()) {
                        console.log(
                          `📏 CYO: Updated unavailable ${optionType.key} option value: ${decodedValue.toLowerCase()}`
                        );
                      }
                    }
                  } else {
                    optionValueElement.textContent = decodedValue.toLowerCase();
                    if (this.allowDebug()) {
                      console.log(
                        `📏 CYO: Updated unavailable ${optionType.key} option value: ${decodedValue.toLowerCase()}`
                      );
                    }
                  }
                } else {
                  optionValueElement.textContent = "";
                }
              }
            });
          }
        }
      } catch (error) {
        console.error("❌ CYO: Error updating option value display:", error);
      }
    };

    /**
     * Update action buttons and header navigation without full re-render
     */
    updateActionButtons = () => {
      // Update footer actions
      const actionsContainer =
        this.shadowRoot.querySelector(".cyo-step__actions");
      if (actionsContainer) {
        actionsContainer.replaceChildren();
        const content = createElementFromHTML(
          `<div>${this.renderActionButtons()}</div>`
        );
        if (content) {
          // Move the children of the wrapper div to the container
          while (content.firstChild) {
            actionsContainer.appendChild(content.firstChild);
          }
        }
      }

      // Update header navigation
      const headerNavContainer =
        this.shadowRoot.querySelector(".cyo-step__nav");
      if (headerNavContainer) {
        headerNavContainer.replaceChildren();
        const content = createElementFromHTML(
          `<div>${this.renderHeaderNavigation(true)}</div>`
        );
        if (content) {
          // Move the children of the wrapper div to the container
          while (content.firstChild) {
            headerNavContainer.appendChild(content.firstChild);
          }
        }
      }

      // No need to reattach listeners - event delegation handles it
    };

    /**
     * Update variant option selection UI
     */
    updateVariantOptionSelection = (variant, selection) => {
      try {
        const product = selection.product;
        const optionTypes = this.getProductOptionTypes(product);

        if (this.allowDebug()) {
          console.log(
            "🎨 CYO: Updating variant option selection UI",
            variant,
            optionTypes
          );
        }

        // Update each option type's selection UI
        optionTypes.forEach((optionType) => {
          let selectedValue;

          // For color with special properties, use variant.color
          if (optionType.specialKey === "color" && product.has_color_option) {
            selectedValue = variant.color;
          } else {
            // For all other cases, use the options array
            selectedValue = variant.options?.[optionType.index];
          }

          if (selectedValue) {
            const dataAttr = `data-${optionType.key}`;
            const optionInputs = this.shadowRoot.querySelectorAll(
              `input[${dataAttr}]`
            );
            const containerClass =
              optionType.key === "color"
                ? ".cyo-color-option"
                : optionType.key === "size"
                ? ".cyo-size-option"
                : `.cyo-${optionType.key}-option`;

            if (this.allowDebug()) {
              console.log(
                `🎨 CYO: Updating ${optionType.key} selection to: ${selectedValue}, found ${optionInputs.length} inputs`
              );
            }

            optionInputs.forEach((input) => {
              const container = input.closest(containerClass);
              if (input.dataset[optionType.key] === selectedValue) {
                input.checked = true;
                const modifierClass =
                  containerClass.replace(".", "") + "--selected";
                container?.classList.add(modifierClass);
                if (this.allowDebug()) {
                  console.log(
                    `✅ CYO: Selected ${optionType.key} option: ${selectedValue}`
                  );
                }
              } else {
                input.checked = false;
                const modifierClass =
                  containerClass.replace(".", "") + "--selected";
                container?.classList.remove(modifierClass);
              }
            });
          }
        });
      } catch (error) {
        console.error(
          "❌ CYO: Error updating variant option selection:",
          error
        );
      }
    };

    /**
     * Handle product selection
     */
    handleProductSelection = (productId, optionId, zoneType = 'regular') => {
      try {
        if (this.allowDebug()) {
          console.log(
            `🎯 CYO: Product selected - ID: ${productId}, Option: ${optionId}, Zone: ${zoneType}`
          );
        }

        const currentStep = this.getCurrentStep();
        if (!currentStep) {
          console.error("❌ CYO: No current step found");
          return;
        }

        // Find option in the appropriate zone
        const sourceOptions = zoneType === 'members_only' ? currentStep.members_only_options : currentStep.options;
        const option = sourceOptions?.find((opt) => opt.id === optionId);
        const product = option?.products.find((prod) => prod.id === productId);

        if (!product || !option) {
          console.error("❌ CYO: Product or option not found", {
            productId,
            optionId,
            currentStep,
          });
          return;
        }

        // Get previous variant selections for smart matching
        const previousSelections = this.getPreviousVariantSelections();

        const selection = {
          step: currentStep,
          option: option,
          product: product,
          variant: null,
          zoneType: zoneType,
          zoneKey: zoneType, // zoneKey is the same as zoneType in current implementation
        };

        // Try to find matching variant before rendering to avoid flash
        let preSelectedVariant = null;
        if (
          Object.keys(previousSelections).some((key) => previousSelections[key])
        ) {
          preSelectedVariant = this.findMatchingVariant(
            product,
            option,
            previousSelections
          );

          if (preSelectedVariant) {
            if (this.allowDebug()) {
              console.log(
                `🤖 CYO: Pre-selecting matching variant: ${
                  preSelectedVariant.title || preSelectedVariant.id
                }`
              );
            }
            selection.variant = preSelectedVariant;
          }
        }

        // Store the selection
        this.setSelectionForStep(currentStep, selection, !!preSelectedVariant);

        // Update UI components
        this.updateProductSwatchSelection(product, option, zoneType);
        this.renderVariantSelection(product, selection, zoneType);
        this.updateOptionValueDisplay();

        // Update combination unavailable states based on current selection
        this.updateCombinationUnavailableStates();

        // If we pre-selected a variant, update action buttons
        if (preSelectedVariant) {
          this.updateActionButtons();
        }

        // Always update media gallery when a product is selected, regardless of variant pre-selection
        // This ensures media updates even during auto-selection after reset
        this.debouncedUpdateMediaGallery();

        if (this.allowDebug()) {
          console.log("✅ CYO: Product selection handling completed");
        }
      } catch (error) {
        console.error("❌ CYO: Error handling product selection:", error);
      }
    };

    /**
     * Update variant selection based on current form values
     */
    updateVariantSelection = (product, selection, availableVariants) => {
      try {
        if (this.allowDebug()) {
          console.log(
            `🔄 CYO: Updating variant selection for product: ${product.title}`
          );
        }

        // Get all option types for this product
        const optionTypes = this.getProductOptionTypes(product);

        // Collect selected values for each option type
        const selectedValues = {};

        optionTypes.forEach((optionType) => {
          const selectedInput = this.shadowRoot.querySelector(
            `input[name*="${optionType.key}-selection"]:checked`
          );
          if (selectedInput) {
            selectedValues[optionType.key] = selectedInput.value;
            if (this.allowDebug()) {
              console.log(
                `🔄 CYO: Found selected ${optionType.key}: ${selectedInput.value}`
              );
            }
          }
        });

        if (this.allowDebug()) {
          console.log(`🔄 CYO: Selected values:`, selectedValues);
        }

        // Find matching variant based on all selected option values
        const matchingVariant = availableVariants.find((variant) => {
          return optionTypes.every((optionType) => {
            const selectedValue = selectedValues[optionType.key];
            if (!selectedValue) return true; // If no selection, don't filter on this option

            if (optionType.key === "color") {
              return variant.color === selectedValue;
            } else {
              // For all other options, check the options array at the correct index
              return variant.options[optionType.index] === selectedValue;
            }
          });
        });

        if (matchingVariant) {
          if (this.allowDebug()) {
            console.log(
              `🔄 CYO: Found matching variant: ${
                matchingVariant.title || matchingVariant.id
              }`
            );
          }
          this.handleVariantSelection(matchingVariant, selection);
        } else {
          if (this.allowDebug()) {
            console.log(`🔄 CYO: No matching variant found - marking as unavailable selection`);
          }
          // Handle case where selected combination is unavailable
          this.handleUnavailableVariantSelection(selectedValues, selection);
        }
      } catch (error) {
        console.error("❌ CYO: Error updating variant selection:", error);
      }
    };

    /**
     * Update product option images based on selected variant options
     * @param {Object} selectedVariant - The currently selected variant
     * @param {Object} selection - The current selection object
     */
    updateProductOptionImages = (selectedVariant, selection) => {
      try {
        if (this.allowDebug()) {
          console.log(
            `🖼️ CYO: Updating product option images based on variant: ${selectedVariant.title}`
          );
        }

        const currentStep = this.getCurrentStep();
        if (!currentStep) return;

        // Get all available options for the current step
        const selectionZones = this.getAvailableOptions(currentStep);

        // Process each zone
        Object.entries(selectionZones).forEach(([zoneKey, zone]) => {
          if (!zone.options || zone.options.length === 0) return;

          // Get the zone element in the DOM
          const zoneElement = this.shadowRoot.querySelector(
            `.cyo-selection-zone[data-zone-key="${zoneKey}"]`
          );
          if (!zoneElement) return;

          // Process each option in the zone
          zone.options.forEach(option => {
            option.products.forEach(product => {
              if (product.id === selection.product.id) {
                // For the selected product, update to show the selected variant
                if (this.allowDebug()) {
                  console.log(
                    `🖼️ CYO: Updating selected product image: ${product.title} to variant ${selectedVariant.title}`
                  );
                }
                this.updateSingleProductOptionImage(
                  zoneElement,
                  product,
                  option,
                  selectedVariant,
                  zoneKey
                );
              } else {
                // For other products, find matching variants
                const matchingVariant = this.findMatchingVariantForImageUpdate(
                  product,
                  selectedVariant,
                  selection.product
                );

                if (matchingVariant) {
                  // Update the product option image
                  this.updateSingleProductOptionImage(
                    zoneElement,
                    product,
                    option,
                    matchingVariant,
                    zoneKey
                  );
                } else {
                  // Reset to default image if no matching variant found
                  this.resetProductOptionImageToDefault(
                    zoneElement,
                    product,
                    option
                  );
                }
              }
            });
          });
        });
      } catch (error) {
        console.error("❌ CYO: Error updating product option images:", error);
      }
    };

    /**
     * Find a matching variant in a product for image updates
     * @param {Object} targetProduct - The product to find a variant in
     * @param {Object} selectedVariant - The currently selected variant
     * @param {Object} selectedProduct - The product the selected variant belongs to
     * @returns {Object|null} Matching variant or null if none found
     */
    findMatchingVariantForImageUpdate = (targetProduct, selectedVariant, selectedProduct) => {
      try {
        // Get the option types that both products share
        const selectedProductOptionTypes = this.getProductOptionTypes(selectedProduct);
        const targetProductOptionTypes = this.getProductOptionTypes(targetProduct);

        // Find matching variants based on shared option values
        const matchingVariant = targetProduct.variants.find(variant => {
          // Check if this variant is available
          if (!this.isVariantAvailable(variant)) {
            return false;
          }

          // Check if variant matches on ALL shared option types
          const sharedOptionTypes = selectedProductOptionTypes.filter(selectedOptionType => {
            return targetProductOptionTypes.some(targetOptionType =>
              targetOptionType.key === selectedOptionType.key
            );
          });

          // If no shared option types, don't update
          if (sharedOptionTypes.length === 0) {
            return false;
          }

          // All shared option types must match
          return sharedOptionTypes.every(selectedOptionType => {
            const targetOptionType = targetProductOptionTypes.find(
              type => type.key === selectedOptionType.key
            );

            if (!targetOptionType) return false;

            // For color options, use the color property for more accurate matching
            if (selectedOptionType.specialKey === 'color' &&
                selectedVariant.color && variant.color) {
              return selectedVariant.color === variant.color;
            }

            // For other options, compare the option values
            const selectedValue = selectedVariant.options[selectedOptionType.index];
            const targetValue = variant.options[targetOptionType.index];

            return selectedValue === targetValue;
          });
        });

        if (this.allowDebug() && matchingVariant) {
          console.log(
            `🔍 CYO: Found matching variant for ${targetProduct.title}: ${matchingVariant.title} (${matchingVariant.color})`
          );
        } else if (this.allowDebug()) {
          console.log(
            `🔍 CYO: No matching variant found for ${targetProduct.title} with selected ${selectedVariant.color}`
          );
        }

        return matchingVariant || null;
      } catch (error) {
        console.error("❌ CYO: Error finding matching variant for image update:", error);
        return null;
      }
    };

    /**
     * Update a single product option image in the DOM
     * @param {Element} zoneElement - The zone DOM element
     * @param {Object} product - The product object
     * @param {Object} option - The option object
     * @param {Object} variant - The variant to show image for
     * @param {string} zoneKey - The zone key
     */
    updateSingleProductOptionImage = async (zoneElement, product, option, variant, zoneKey) => {
      try {
        // Find the specific product option element
        const productOptionElement = zoneElement.querySelector(
          `input[data-product-id="${product.id}"][data-option-id="${option.id}"]`
        );

        if (!productOptionElement) return;

        const labelElement = productOptionElement.nextElementSibling;
        if (!labelElement) return;

        const imgElement = labelElement.querySelector('img');
        if (!imgElement) return;

        // Fetch the variant-specific media
        if (variant.media_url) {
          try {
            const response = await fetch(variant.media_url);
            if (response.ok) {
              const mediaHtml = await response.text();

              // Extract the img tag from the response
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = mediaHtml;
              const newImgElement = tempDiv.querySelector('img');

              if (newImgElement) {
                // Update the existing img element attributes
                imgElement.src = newImgElement.src;
                imgElement.srcset = newImgElement.srcset || '';
                imgElement.alt = newImgElement.alt || '';

                if (this.allowDebug()) {
                  console.log(
                    `🖼️ CYO: Updated image for product ${product.title} to variant ${variant.title}`
                  );
                }
              }
            }
          } catch (fetchError) {
            console.error(`❌ CYO: Error fetching variant media for ${product.title}:`, fetchError);
          }
        }
      } catch (error) {
        console.error("❌ CYO: Error updating single product option image:", error);
      }
    };

    /**
     * Reset a product option image to its default (first media item)
     * @param {Element} zoneElement - The zone DOM element
     * @param {Object} product - The product object
     * @param {Object} option - The option object
     */
    resetProductOptionImageToDefault = (zoneElement, product, option) => {
      try {
        // Find the specific product option element
        const productOptionElement = zoneElement.querySelector(
          `input[data-product-id="${product.id}"][data-option-id="${option.id}"]`
        );

        if (!productOptionElement) return;

        const labelElement = productOptionElement.nextElementSibling;
        if (!labelElement) return;

        const imgElement = labelElement.querySelector('img');
        if (!imgElement) return;

        // Reset to the default image (first media item)
        if (product.media && product.media[0]) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = product.media[0];
          const defaultImgElement = tempDiv.querySelector('img');

          if (defaultImgElement) {
            // Update the existing img element attributes
            imgElement.src = defaultImgElement.src;
            imgElement.srcset = defaultImgElement.srcset || '';
            imgElement.alt = defaultImgElement.alt || '';

            if (this.allowDebug()) {
              console.log(
                `🖼️ CYO: Reset image for product ${product.title} to default`
              );
            }
          }
        }
      } catch (error) {
        console.error("❌ CYO: Error resetting product option image to default:", error);
      }
    };

    /**
     * Handle selection of unavailable variant combination
     * @param {Object} selectedValues - The selected option values that don't match any variant
     * @param {Object} selection - The current selection object
     */
    handleUnavailableVariantSelection = (selectedValues, selection) => {
      try {
        if (this.allowDebug()) {
          console.log(`🚫 CYO: Handling unavailable variant selection:`, selectedValues);
        }

        // Set variant to null to indicate unavailable combination
        selection.variant = null;

        // Store the selected values for display purposes even when variant is unavailable
        selection.selectedValues = selectedValues;

        // Store the selection with null variant (indicating unavailable)
        this.setSelectionForStep(selection.step, selection, false);

        // Update the UI to show the selected options even though they're unavailable
        this.updateUnavailableOptionSelection(selectedValues, selection);

        // Update action buttons to show "SOLD OUT"
        this.updateActionButtons();

        // Update option value display to show the selected unavailable options
        this.updateOptionValueDisplay();

        // Update combination unavailable states to ensure all unavailable options are marked
        this.updateCombinationUnavailableStates();

        // Update media gallery (might fall back to step 1 media)
        this.debouncedUpdateMediaGallery();

        if (this.allowDebug()) {
          console.log(`✅ CYO: Unavailable variant selection handled`);
        }
      } catch (error) {
        console.error("❌ CYO: Error handling unavailable variant selection:", error);
      }
    };

    /**
     * Update UI to show selected options for unavailable combinations
     * Marks all selected options as sold out since the combination is unavailable
     * @param {Object} selectedValues - The selected option values
     * @param {Object} selection - The current selection object
     */
    updateUnavailableOptionSelection = (selectedValues, selection) => {
      try {
        const product = selection.product;
        const optionTypes = this.getProductOptionTypes(product);

        if (this.allowDebug()) {
          console.log("🎨 CYO: Updating unavailable option selection UI", selectedValues);
        }

        // Get all currently checked inputs to mark them as sold out
        const allCheckedInputs = this.shadowRoot.querySelectorAll('input[type="radio"]:checked');

        if (this.allowDebug()) {
          console.log(`🎨 CYO: Found ${allCheckedInputs.length} currently checked inputs to mark as sold out`);
        }

        // Mark all currently selected options as sold out
        allCheckedInputs.forEach((input) => {
          const container = input.closest('.cyo-color-option, .cyo-size-option, .cyo-product-option') ||
                           input.closest(`[class*="cyo-"][class*="-option"]`);
          if (container) {
            // Add sold out class to the container
            container.classList.add('cyo-option--combination-unavailable');

            if (this.allowDebug()) {
              console.log(`🚫 CYO: Marked option as combination unavailable:`, input.name, input.value);
            }
          }
        });

        // Update each option type's selection UI based on selectedValues
        optionTypes.forEach((optionType) => {
          const selectedValue = selectedValues[optionType.key];

          if (selectedValue !== undefined) { // Check if this option type was selected
            const dataAttr = `data-${optionType.key}`;
            const optionInputs = this.shadowRoot.querySelectorAll(
              `input[${dataAttr}]`
            );
            const containerClass =
              optionType.key === "color"
                ? ".cyo-color-option"
                : optionType.key === "size"
                ? ".cyo-size-option"
                : `.cyo-${optionType.key}-option`;

            if (this.allowDebug()) {
              console.log(
                `🎨 CYO: Updating ${optionType.key} unavailable selection to: ${selectedValue}, found ${optionInputs.length} inputs`
              );
            }

            optionInputs.forEach((input) => {
              const container = input.closest(containerClass);
              if (input.dataset[optionType.key] === selectedValue) {
                input.checked = true;
                const modifierClass =
                  containerClass.replace(".", "") + "--selected";
                container?.classList.add(modifierClass);
                if (this.allowDebug()) {
                  console.log(
                    `✅ CYO: Selected unavailable ${optionType.key} option: ${selectedValue}`
                  );
                }
              } else {
                input.checked = false;
                const modifierClass =
                  containerClass.replace(".", "") + "--selected";
                container?.classList.remove(modifierClass);
              }
            });
          }
        });
      } catch (error) {
        console.error("❌ CYO: Error updating unavailable option selection UI:", error);
      }
    };

    /**
     * Update combination unavailable states for all option types based on current partial selection
     */
    updateCombinationUnavailableStates = () => {
      try {
        const currentStep = this.getCurrentStep();
        const selection = this.getSelectionForStep(currentStep);

        if (!selection || !selection.product) {
          if (this.allowDebug()) {
            console.log(`ℹ️ CYO: No selection or product found, skipping combination unavailable state update`);
          }
          return;
        }

        const product = selection.product;
        const optionTypes = this.getProductOptionTypes(product);

        if (this.allowDebug()) {
          console.log(`🔍 CYO: Updating combination unavailable states for product: ${product.title}`);
        }

        // Clear all existing combination unavailable states first
        this.clearCombinationUnavailableState();

        // Get current selected values
        const currentSelectedValues = {};
        optionTypes.forEach(optionType => {
          const fieldsetName = `${optionType.key}-selection-${product.id}`;
          const selectedInput = this.shadowRoot.querySelector(`input[name="${fieldsetName}"]:checked`);
          if (selectedInput) {
            currentSelectedValues[optionType.key] = selectedInput.value;
          }
        });

        if (this.allowDebug()) {
          console.log(`📊 CYO: Current selected values:`, currentSelectedValues);
        }

        // For each option type, check which values would create unavailable combinations
        optionTypes.forEach(optionType => {
          const fieldsetName = `${optionType.key}-selection-${product.id}`;
          const optionInputs = this.shadowRoot.querySelectorAll(`input[name="${fieldsetName}"]`);

          optionInputs.forEach(input => {
            const optionValue = input.value;
            const container = input.closest('.cyo-color-option, .cyo-size-option, .cyo-product-option') ||
                             input.closest(`[class*="cyo-"][class*="-option"]`);

            if (!container) return;

            // Create a test combination with this option value
            const testCombination = { ...currentSelectedValues };
            testCombination[optionType.key] = optionValue;

            // Check if this combination has any available variants
            const hasAvailableVariant = product.variants.some(variant => {
              if (!this.isVariantAvailable(variant)) return false;

              // Check if this variant matches the test combination
              return optionTypes.every(ot => {
                const testValue = testCombination[ot.key];
                if (!testValue) return true; // Not selected yet, so it matches

                if (ot.specialKey === "color" && product.has_color_option) {
                  return variant.color === testValue;
                } else {
                  return variant.options[ot.index] === testValue;
                }
              });
            });

            // If no available variant exists for this combination, mark as combination unavailable
            if (!hasAvailableVariant) {
              container.classList.add('cyo-option--combination-unavailable');

              if (this.allowDebug()) {
                console.log(`🚫 CYO: Marked ${optionType.key} option "${optionValue}" as combination unavailable`);
              }
            }
          });
        });

        if (this.allowDebug()) {
          console.log(`✅ CYO: Updated combination unavailable states`);
        }
      } catch (error) {
        console.error("❌ CYO: Error updating combination unavailable states:", error);
      }
    };

    /**
     * Clear combination unavailable state from all options
     * Called when a valid variant combination is selected
     */
    clearCombinationUnavailableState = () => {
      try {
        const allOptionContainers = this.shadowRoot.querySelectorAll('.cyo-color-option, .cyo-size-option, .cyo-product-option, [class*="cyo-"][class*="-option"]');

        allOptionContainers.forEach((container) => {
          container.classList.remove('cyo-option--combination-unavailable');

          if (this.allowDebug()) {
            console.log(`✅ CYO: Cleared combination unavailable state from option container`);
          }
        });
      } catch (error) {
        console.error("❌ CYO: Error clearing combination unavailable state:", error);
      }
    };

    /**
     * Handle variant selection
     */
    handleVariantSelection = (variant, selection) => {
      try {
        if (this.allowDebug()) {
          console.log(
            `🎨 CYO: Variant selected - ${
              variant.title || variant.id
            } for product "${selection.product.title}"`
          );
        }

        selection.variant = variant;

        // Update combination unavailable states since we now have a valid variant
        this.updateCombinationUnavailableStates();

        // Update price when variant is selected (complete selection)
        this.setSelectionForStep(selection.step, selection, true);

        // Update the action buttons container only
        this.updateActionButtons();

        // Update the variant option UI to show selection
        this.updateVariantOptionSelection(variant, selection);

        // Update the option value display (consolidate all display updates here)
        this.updateOptionValueDisplay();

        // Update product option images based on variant selection
        this.updateProductOptionImages(variant, selection);

        // Update media gallery based on current step
        this.debouncedUpdateMediaGallery();
      } catch (error) {
        console.error("❌ CYO: Error handling variant selection:", error);
      }
    };

    /**
     * Handle forward navigation
     */
    handleNext = async () => {
      try {
        const currentStep = this.getCurrentStep();
        const selection = this.getSelectionForStep(currentStep);

        if (!selection || !selection.variant) {
          if (this.allowDebug()) {
            console.warn("⚠️ CYO: Cannot proceed - no selection or variant");
          }
          this.announceToScreenReader(
            "Please make a selection before continuing",
            "assertive"
          );
          return;
        }

        // Check if the selected variant is available
        if (!this.isVariantAvailable(selection.variant)) {
          if (this.allowDebug()) {
            console.warn("⚠️ CYO: Cannot proceed - selected variant is sold out");
          }
          this.announceToScreenReader(
            "This item is sold out. Please select a different option.",
            "assertive"
          );
          return;
        }

        // Reset zone expansion state when navigating
        this.zoneExpansionState = {};

        this.currentStepIndex++;
        await this.render();

        // Update media gallery after navigation (slight delay to let DOM settle)
        setTimeout(() => {
          this.updateMediaGallery();
        }, 100);

        // Announce navigation for screen readers
        if (this.isReviewStep()) {
          this.announceToScreenReader("Moved to review step", "assertive");
        } else {
          const nextStep = this.getCurrentStep();
          const stepName =
            nextStep?.label || `Step ${this.currentStepIndex + 1}`;
          this.announceToScreenReader(`Moved to ${stepName}`, "assertive");
        }
      } catch (error) {
        console.error("❌ CYO: Error handling next:", error);
      }
    };

    /**
     * Handle backward navigation
     * Enhanced with accessibility announcements
     */
    handleBack = async () => {
      try {
        if (this.currentStepIndex > 0) {
          // Reset zone expansion state when navigating
          this.zoneExpansionState = {};

          this.currentStepIndex--;
          await this.render();

          // Update media gallery after navigation (slight delay to let DOM settle)
          setTimeout(() => {
            this.updateMediaGallery();
          }, 100);

          // Announce navigation for screen readers
          const currentStep = this.getCurrentStep();
          const stepName =
            currentStep?.label || `Step ${this.currentStepIndex + 1}`;
          this.announceToScreenReader(`Moved back to ${stepName}`, "assertive");
        }
      } catch (error) {
        console.error("❌ CYO: Error handling back:", error);
      }
    };

    /**
     * Reset component to initial state
     * Clears all selections and returns to step 1
     */
    resetComponent = async () => {
      try {
        if (this.allowDebug()) {
          console.log("🔄 CYO: Resetting component to initial state");
        }

        // Reset navigation state
        this.currentStepIndex = 0;
        this.currentPath = [];

        // Clear all selections
        this.selections.clear();

        // Reset pricing state
        this.price = 0;
        this.compareAtPrice = 0;
        this.priceHTML = null;

        // Clear caches and reset state
        this.#productOptionTypesCache.clear();
        this.currentStep1MediaUrl = null;
        this.currentDisplayedMediaUrl = null;
        this.currentBundleFeaturedMedia = null;
        this.zoneExpansionState = {};

        // Clear any cart errors
        this.clearCartError();

        // Re-render the component from step 1
        await this.render();

        if (this.allowDebug()) {
          console.log("✅ CYO: Component reset completed");
        }
      } catch (error) {
        console.error("❌ CYO: Error resetting component:", error);
      }
    };

    /**
     * Handle Add to Bag
     */
    handleAddToBag = async () => {
      try {
        if (this.allowDebug()) {
          console.log("🛒 CYO: Add to Bag triggered");
        }

        const selections = Array.from(this.selections.values());
        const validSelections = selections.filter(
          (selection) => selection.variant
        );

        if (validSelections.length === 0) {
          console.error("❌ CYO: No variants selected for cart");
          return;
        }

        if (this.allowDebug()) {
          console.log(
            `🛒 CYO: Cart transformation mode: ${this.cartTransformation}`
          );
          console.log("🛒 CYO: Valid selections:", validSelections);
        }

        // Prepare cart items based on transformation type
        const cartItems = this.prepareCartItems(
          validSelections,
          this.cartTransformation
        );

        if (cartItems.length === 0) {
          console.error("❌ CYO: Failed to prepare cart items");
          this.setAddingToBagState(false);
          this.showCartError("There was a problem building your CYO product. Please try again.");
          return;
        }

        if (this.allowDebug()) {
          console.log("🛒 CYO: Cart items:", cartItems);
        }

        // Set loading state
        this.setAddingToBagState(true);

        if (this.allowDebug()) {
          console.log("🛒 CYO: Submitting cart items:", cartItems);
        }

        // Capture component + parent data for analytics before dispatching.
        // A merge transformation collapses these components into the parent
        // line, so this is the last point at which their product data exists.
        // See utilities-bundle-analytics.js.
        await this.captureBundleAnalytics(validSelections, cartItems);

        // Submit to cart using theme utility
        const { addToCartViaStandardAction } = await import('@utilities/theme');
        const cartResponse = await addToCartViaStandardAction(cartItems, { context: 'product' });

        // Reset loading state
        this.setAddingToBagState(false);

        // A rejected mutation resolves with userErrors; a failed request throws
        // and is handled by the catch below.
        if (cartResponse?.userErrors?.length > 0) {
          // A rejected mutation must not report — drop the capture taken above.
          await this.clearBundleAnalytics();

          // Handle cart error
          this.showCartError(
            cartResponse.userErrors[0].message ||
              "An error occurred while adding to cart"
          );

          if (this.allowDebug()) {
            console.error("🛒 CYO: Cart error:", cartResponse);
          }
          return;
        }

        if (this.allowDebug()) {
          console.log("🛒 CYO: Cart response:", cartResponse);
        }

        // Clear any existing error messages on success
        this.clearCartError();

        if (this.allowDebug()) {
          console.log("🛒 CYO: Successfully added to cart", {
            cartItems,
            cartTransformation: this.cartTransformation,
          });
        }

        // Reset component to initial state after successful add to bag
        this.resetComponent();
      } catch (error) {
        console.error("❌ CYO: Error handling Add to Bag:", error);
        this.setAddingToBagState(false);

        // A failed add must not report — drop any capture taken above.
        await this.clearBundleAnalytics();

        this.showCartError("There was a problem adding your CYO product to the bag. Please try again.");
      }
    };

    /**
     * Capture component and parent product data for the GA addToCart events.
     *
     * Merge only. An expand transformation dispatches a single line and lists
     * its components inside `_bundled_data` for Shopify to expand server-side,
     * so those components never pass through an add this theme issues and have
     * nothing to capture here.
     *
     * The parent's price is derived from the components by the capture helper
     * (aggregate pricing), so it isn't passed.
     *
     * Failures are swallowed: analytics must never block an add to bag.
     *
     * @param {Array} validSelections - Selections with a resolved variant
     * @param {Array} cartItems - The prepared items being sent, which carry the
     *   `_bundled_id` generated inside prepareCartItems
     */
    captureBundleAnalytics = async (validSelections, cartItems) => {
      try {
        if (this.cartTransformation !== "merge") return;

        const { captureBundleAdd } = await import('@utilities/bundle-analytics');

        const components = [];
        for (const selection of validSelections) {
          const variant = selection?.variant;
          const variantId = normalizeVariantId(variant?.id);
          if (!variantId) continue;

          const product = selection.product || {};
          components.push({
            variantId,
            price: variant.price || 0,
            quantity: 1,
            variantTitle: variant.title || '',
            product: {
              id: product.id ?? null,
              title: product.title || '',
              vendor: product.vendor || '',
              type: product.type || '',
            },
          });
        }

        const bundleProduct = this.config.bundle_product || {};
        captureBundleAdd({
          bundleId: cartItems?.[0]?.properties?._bundled_id || null,
          components,
          parent: {
            variantId: normalizeVariantId(bundleProduct.default_variant_id),
            variantTitle: '',
            product: {
              id: bundleProduct.id ?? null,
              title: bundleProduct.title || '',
              vendor: bundleProduct.vendor || '',
              type: bundleProduct.type || '',
            },
          },
        });
      } catch (error) {
        console.warn("⚠️ CYO: analytics capture failed", error);
      }
    };

    /**
     * Discard a pending analytics capture after a failed add, so a rejected
     * add never reports.
     */
    clearBundleAnalytics = async () => {
      try {
        const { clearBundleCapture } = await import('@utilities/bundle-analytics');
        clearBundleCapture();
      } catch (error) {
        console.warn("⚠️ CYO: analytics clear failed", error);
      }
    };

    /**
     * Debounced media gallery update to prevent rapid successive calls
     */
    debouncedUpdateMediaGallery = (delay = this.mediaUpdateDelay) => {
      this.debounceManager.debounce(
        "mediaUpdate",
        () => {
          this.updateMediaGallery();
        },
        delay
      );

      if (this.allowDebug()) {
        console.log(`🖼️ CYO: Media update debounced (${delay}ms)`);
      }
    };

    /**
     * Update media gallery based on current step and selections
     */
    updateMediaGallery = async () => {
      try {
        const productMediaComponent =
          this.productOverview?.querySelector("product-media");

        if (!productMediaComponent) {
          if (this.allowDebug()) {
            console.warn(
              "⚠️ CYO: No product-media component found for media update"
            );
          }
          return;
        }

        let mediaUrl = null;

        if (this.currentStepIndex === 0) {
          // Step 1: Use variant media_url from the selected variant
          const currentStep = this.getCurrentStep();
          const selection = this.getSelectionForStep(currentStep);

          if (selection && selection.variant && selection.variant.media_url) {
            mediaUrl = selection.variant.media_url;
            this.currentStep1MediaUrl = mediaUrl; // Track step 1 media for fallback

            // Track step 1 featured media for bundle
            this.updateBundleFeaturedMedia(selection.variant);

            if (this.allowDebug()) {
              console.log(
                `🖼️ CYO: Step 1 - Using variant media from: ${mediaUrl}`
              );
            }
          }
        } else {
          // Step 2+: Use aggregate media galleries
          const aggregateResult = this.getAggregateMediaUrlWithMetadata();
          mediaUrl = aggregateResult?.mediaUrl || null;

          // Track aggregate featured media for bundle
          if (aggregateResult?.aggregateGallery) {
            this.updateBundleFeaturedMediaFromAggregate(
              aggregateResult.aggregateGallery
            );
          }

          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Step ${
                this.currentStepIndex + 1
              } - Using aggregate media from: ${mediaUrl || "none found"}`
            );
          }
        }

        if (mediaUrl) {
          // Use the product-media component's updateMedia method
          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Updating product-media component with URL: ${mediaUrl}`
            );
          }

          await productMediaComponent.updateMedia(mediaUrl);
          this.currentDisplayedMediaUrl = mediaUrl;

          if (this.allowDebug()) {
            console.log(
              `✅ CYO: Successfully updated media via product-media component`
            );
          }
        } else {
          // No aggregate found, fall back to step 1 media if available
          const fallbackUrl = this.currentStep1MediaUrl;

          if (fallbackUrl && fallbackUrl !== this.currentDisplayedMediaUrl) {
            if (this.allowDebug()) {
              console.log(
                `🖼️ CYO: No aggregate found, falling back to step 1 media: ${fallbackUrl}`
              );
            }
            await productMediaComponent.updateMedia(fallbackUrl);
            this.currentDisplayedMediaUrl = fallbackUrl;
          } else if (this.allowDebug()) {
            if (fallbackUrl === this.currentDisplayedMediaUrl) {
              console.log(
                "🖼️ CYO: Fallback media already displayed, skipping update"
              );
            } else {
              console.log(
                "🖼️ CYO: No media URL found and no fallback available"
              );
            }
          }
        }
      } catch (error) {
        console.error("❌ CYO: Error updating media gallery:", error);
      }
    };

    /**
     * Update bundle featured media from a variant (step 1)
     * @param {Object} variant - The variant with featured media info
     */
    updateBundleFeaturedMedia = (variant) => {
      try {
        if (variant?.featured_media?.id) {
          this.currentBundleFeaturedMedia = {
            type: "variant",
            id: variant.featured_media.id,
            gid: `gid://shopify/MediaImage/${variant.featured_media.id}`,
            url: variant.featured_media.src || variant.featured_media.url,
            alt: variant.featured_media.alt || variant.title || "",
            source: "step1_variant",
          };

          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Updated bundle featured media from variant:`,
              this.currentBundleFeaturedMedia
            );
          }
        }
      } catch (error) {
        console.error(
          "❌ CYO: Error updating bundle featured media from variant:",
          error
        );
      }
    };

    /**
     * Update bundle featured media from aggregate gallery
     * @param {Object} aggregateGallery - The aggregate gallery object
     */
    updateBundleFeaturedMediaFromAggregate = (aggregateGallery) => {
      try {
        // Use the first media from the aggregate gallery as featured image
        const firstMedia =
          aggregateGallery.media?.[0] || aggregateGallery.featured_media;

        if (firstMedia?.id) {
          this.currentBundleFeaturedMedia = {
            type: "aggregate",
            id: firstMedia.id,
            gid: `gid://shopify/MediaImage/${firstMedia.id}`,
            url: firstMedia.src || firstMedia.url,
            alt: firstMedia.alt || `${aggregateGallery.handle} bundle`,
            source: "aggregate_gallery",
            aggregateHandle: aggregateGallery.handle,
          };

          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Updated bundle featured media from aggregate:`,
              this.currentBundleFeaturedMedia
            );
          }
        } else if (this.allowDebug()) {
          console.warn(
            "⚠️ CYO: No featured media found in aggregate gallery:",
            aggregateGallery
          );
        }
      } catch (error) {
        console.error(
          "❌ CYO: Error updating bundle featured media from aggregate:",
          error
        );
      }
    };

    /**
     * Prepare cart items based on cart transformation type
     * @param {Array} selections - Valid selections with variants
     * @param {string} cartTransformation - 'merge' or 'expand'
     * @returns {Array} Prepared cart items for submission
     */
    prepareCartItems = (selections, cartTransformation) => {
      try {
        const bundleId = this.generateBundleId();
        const featuredMedia = this.determineFeaturedMedia(selections);

        if (this.allowDebug()) {
          console.log(`🛒 CYO: Generated bundle ID: ${bundleId}`);
          console.log(`🛒 CYO: Featured media: ${featuredMedia}`);
        }

        if (cartTransformation === "expand") {
          return this.prepareExpandItems(selections, bundleId);
        } else {
          // Default to 'merge'
          return this.prepareMergeItems(selections, bundleId);
        }
      } catch (error) {
        console.error("❌ CYO: Error preparing cart items:", error);
        return [];
      }
    };

    /**
     * Generate a unique bundle ID
     * @returns {string} Unique bundle identifier
     */
    generateBundleId = () => {
      // Generate a unique bundle ID using timestamp + random component
      const timestamp = Date.now();
      const random = Math.floor(Math.random() * 10000);
      return `cyo_bundle_${timestamp}_${random}`;
    };

    /**
     * Collect parent product properties from product-overview component
     * @returns {Object} Object containing parent product properties
     */
    collectParentProductProperties = () => {
      try {
        // Find the product-overview component on the page
        const productOverview = document.querySelector('product-overview');

        if (productOverview && typeof productOverview.collectLineItemProperties === 'function') {
          const parentProperties = productOverview.collectLineItemProperties();

          if (this.allowDebug()) {
            console.log('🛒 CYO: Collected parent product properties:', parentProperties);
          }

          return parentProperties;
        }

        if (this.allowDebug()) {
          console.log('🛒 CYO: No product-overview component found or collectLineItemProperties method not available');
        }

        return {};
      } catch (error) {
        console.error('❌ CYO: Error collecting parent product properties:', error);
        return {};
      }
    };

    /**
     * Determine featured media for the bundle
     * @param {Array} selections - Valid selections
     * @returns {string|null} Featured media GraphQL ID or null
     */
    determineFeaturedMedia = (selections) => {
      try {
        // Use the currently tracked bundle featured media if available
        if (this.currentBundleFeaturedMedia?.gid) {
          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Using tracked bundle featured media: ${this.currentBundleFeaturedMedia.gid} (${this.currentBundleFeaturedMedia.source})`
            );
          }
          return this.currentBundleFeaturedMedia.gid;
        }

        // Fallback to the first selection's variant or product media
        const firstSelection = selections[0];
        if (firstSelection?.variant?.featured_media?.id) {
          const gid = `gid://shopify/MediaImage/${firstSelection.variant.featured_media.id}`;
          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Using fallback featured media from first selection variant: ${gid}`
            );
          }
          return gid;
        } else if (firstSelection?.product?.featured_media?.id) {
          const gid = `gid://shopify/MediaImage/${firstSelection.product.featured_media.id}`;
          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Using fallback featured media from first selection product: ${gid}`
            );
          }
          return gid;
        }

        if (this.allowDebug()) {
          console.warn("⚠️ CYO: No featured media found for bundle");
        }
        return null;
      } catch (error) {
        console.error("❌ CYO: Error determining featured media:", error);
        return null;
      }
    };

    /**
     * Prepare cart items for 'expand' transformation
     * @param {Array} selections - Valid selections
     * @param {string} bundleId - Unique bundle ID
     * @returns {Array} Cart items for expand transformation
     */
    prepareExpandItems = (selections, bundleId) => {
      try {
        if (!this.config.bundle_product?.id) {
          throw new Error("Bundle product ID not found in config");
        }

        const parentVariantId = normalizeVariantId(this.config.bundle_product?.default_variant_id);
        if (!parentVariantId) {
          throw new Error("Bundle parent variant ID is invalid");
        }

        const bundleCost = this.price;

        // Create bundled variants data for expand mode
        const bundledVariants = selections
          .map((selection) => {
            const variantId = normalizeVariantId(selection?.variant?.id);
            if (!variantId) return null;
            return {
              variant_gid: `gid://shopify/ProductVariant/${variantId}`,
              variant_price: selection.variant.price || 0,
            };
          })
          .filter(Boolean);

        if (bundledVariants.length === 0) {
          throw new Error("No valid bundled variants found for expand transformation");
        }

        // Create the bundled data object
        const bundledData = {
          _bundled_id: bundleId,
          _bundle_parent: `gid://shopify/ProductVariant/${parentVariantId}`,
          _bundle_title: this.config.bundle_product.title,
          _bundle_cost: bundleCost,
          _bundled_variants: bundledVariants,
          _bundled_featured_media: this.currentBundleFeaturedMedia
            ? this.currentBundleFeaturedMedia
            : null,
        };


        // Collect parent product properties from product-overview component
        const parentProperties = this.collectParentProductProperties();

        // Create the single cart item (bundle parent product)
        const cartItem = {
          id: parentVariantId,
          quantity: 1,
          properties: {
            _cyo_bundle: true,
            _bundled_id: bundleId,
            _bundled_type: this.cartTransformation,
            _bundled_data: JSON.stringify(bundledData),
            ...parentProperties, // Include parent product properties
          },
        };

        // override the add to cart component from the parent product
        cartItem.properties["_add_to_cart_component"] = "cyo-product";

        if (this.allowDebug()) {
          console.log("🛒 CYO: Prepared expand cart item:", cartItem);
          console.log("🛒 CYO: Bundled variants:", bundledVariants);
          console.log("🛒 CYO: Bundle cost:", bundleCost);
        }

        return [cartItem];
      } catch (error) {
        console.error("❌ CYO: Error preparing expand items:", error);
        return [];
      }
    };

    /**
     * Prepare cart items for 'merge' transformation
     * @param {Array} selections - Valid selections
     * @param {string} bundleId - Unique bundle ID
     * @returns {Array} Cart items for merge transformation
     */
    prepareMergeItems = (selections, bundleId) => {
      try {
        if (!this.config.bundle_product?.id) {
          throw new Error("Bundle product ID not found in config");
        }

        const parentVariantId = normalizeVariantId(this.config.bundle_product?.default_variant_id);
        if (!parentVariantId) {
          throw new Error("Bundle parent variant ID is invalid");
        }

        const cartItems = [];

        // Collect parent product properties once for all items
        const parentProperties = this.collectParentProductProperties();

        const bundleCost = this.price;

        for (const selection of selections) {
          const selectionVariantId = normalizeVariantId(selection?.variant?.id);
          if (!selectionVariantId) continue;

          // Create bundled data for each item
          const bundledData = {
            _bundled_id: bundleId,
            _bundle_parent: `gid://shopify/ProductVariant/${parentVariantId}`,
            _bundle_title: this.config.bundle_product.title,
            _bundle_cost: bundleCost,
            _bundled_component_price: selection.variant.price || 0,
            _bundled_featured_media: this.currentBundleFeaturedMedia
              ? this.currentBundleFeaturedMedia
              : null,
          };

          // Create cart item for this variant
          const cartItem = {
            id: selectionVariantId,
            quantity: 1,
            properties: {
              _cyo_bundle: true,
              _bundled_id: bundleId,
              _bundled_type: this.cartTransformation,
              _bundled_data: JSON.stringify(bundledData),
              ...parentProperties, // Include parent product properties
            },
          };

          // override the add to cart component from the parent product
          cartItem.properties["_add_to_cart_component"] = "cyo-product";

          // Add color property if available
          if (selection.variant.color) {
            cartItem.properties._color_id = selection.variant.color;
          }

          cartItems.push(cartItem);
        }

        if (cartItems.length === 0) {
          throw new Error("No valid merge cart items found");
        }

        if (this.allowDebug()) {
          console.log("🛒 CYO: Prepared merge cart items:", cartItems);
          console.log("🛒 CYO: Bundle cost:", bundleCost);
        }

        return cartItems;
      } catch (error) {
        console.error("❌ CYO: Error preparing merge items:", error);
        return [];
      }
    };

    /**
     * Set loading state for action buttons
     * @param {string} selector - CSS selector for buttons to target
     * @param {boolean} isLoading - Whether the button is in loading state
     * @param {string} loadingText - Text to show when loading
     * @param {string} defaultText - Text to show when not loading
     */
    setActionButtonLoadingState = (
      selector,
      isLoading,
      loadingText,
      defaultText
    ) => {
      try {
        const buttons = this.shadowRoot.querySelectorAll(selector);

        buttons.forEach((button) => {
          const buttonText = button.querySelector(
            ".cyo-step__action-text, .cyo-step__nav-text"
          );
          const spinner = button.querySelector(".loading-overlay__spinner");

          button.classList.toggle("loading", isLoading);
          button.toggleAttribute("disabled", isLoading);
          button.toggleAttribute("aria-disabled", isLoading);

          if (buttonText && loadingText && defaultText) {
            buttonText.textContent = isLoading ? loadingText : defaultText;
          }

          if (spinner) {
            spinner.classList.toggle("hidden", !isLoading);
          }
        });

        if (this.allowDebug()) {
          console.log(
            `🔄 CYO: Set loading state for ${selector}: ${isLoading}`
          );
        }
      } catch (error) {
        console.error("❌ CYO: Error setting button loading state:", error);
      }
    };

    /**
     * Set adding to bag loading state
     * @param {boolean} isAdding - Whether the component is adding to bag
     */
    setAddingToBagState = (isAdding) => {
      this.setActionButtonLoadingState(
        ".cyo-step__action--add-to-bag",
        isAdding,
        "Adding to Bag...",
        "Add to Bag"
      );
    };

    /**
     * Show cart error message above the action buttons
     * Enhanced with accessibility features
     * @param {string} errorMessage - The error message to display
     */
    showCartError = (errorMessage) => {
      try {
        const errorContainer = this.shadowRoot.querySelector(
          ".cyo-cart-error-container"
        );
        if (!errorContainer) {
          if (this.allowDebug()) {
            console.warn("⚠️ CYO: Error container not found");
          }
          return;
        }

        errorContainer.innerHTML = `
          <div class="cyo-cart-error" role="alert" aria-live="assertive">
            <span class="visually-hidden">Error: </span>
            <p class="cyo-cart-error__message">${errorMessage}</p>
          </div>
        `;
        errorContainer.style.display = "block";

        // Announce error to screen readers
        this.announceToScreenReader(`Error: ${errorMessage}`, "assertive");

        // Focus the error message for keyboard users
        const errorElement = errorContainer.querySelector(".cyo-cart-error");
        if (errorElement && this.keyboardNavigation) {
          errorElement.tabIndex = -1;
          errorElement.focus();
        }

        // Scroll error into view if needed
        errorContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });

        if (this.allowDebug()) {
          console.log("🚨 CYO: Cart error displayed:", errorMessage);
        }
      } catch (error) {
        console.error("❌ CYO: Error showing cart error:", error);
      }
    };

    /**
     * Clear any existing cart error message
     */
    clearCartError = () => {
      try {
        const errorContainer = this.shadowRoot.querySelector(
          ".cyo-cart-error-container"
        );
        if (errorContainer) {
          errorContainer.innerHTML = "";
          errorContainer.style.display = "none";

          if (this.allowDebug()) {
            console.log("✅ CYO: Cart error cleared");
          }
        }
      } catch (error) {
        console.error("❌ CYO: Error clearing cart error:", error);
      }
    };

    /**
     * Render error message
     */
    renderError = (message) => {
      const errorHTML = `
        <div class="cyo-error">
          <h3>Error</h3>
          <p>${message}</p>
        </div>
      `;

      // Shadow DOM should already be setup, but ensure container exists
      const container = this.shadowRoot.getElementById("cyo-content");
      if (container) {
        container.replaceChildren();
        const content =
          createElementFromHTML(errorHTML);
        if (content) {
          container.appendChild(content);
        }
      } else {
        // Fallback: setup shadow DOM if somehow not initialized
        this.#setupShadowDOM();
        const newContainer = this.shadowRoot.getElementById("cyo-content");
        if (newContainer) {
          const content =
            createElementFromHTML(errorHTML);
          if (content) {
            newContainer.appendChild(content);
          }
        }
      }
    };

    /**
     * Handle sticky ATB attribute changes
     */
    handleStickyATBChange = () => {
      const newEnableStickyATB = this.getAttribute('data-enable-sticky-atb') == "true" ? true : false;
      const newEnableStickyATBOn = this.getAttribute('data-enable-sticky-atb-on') || 'scroll';

      if (newEnableStickyATB != this.enableStickyATB || newEnableStickyATBOn != this.enableStickyATBOn) {
        this.enableStickyATB = newEnableStickyATB;
        this.enableStickyATBOn = newEnableStickyATBOn;
        this.toggleStickyATBFunctionality();
      }
    }

    /**
     * Toggle sticky ATB functionality on/off
     */
    toggleStickyATBFunctionality = () => {
      if (this.enableStickyATB) {
        this.enableStickyATBEvents();
        this.handleStickyATC();
      } else {
        this.disableStickyATBEvents();
      }
    }

    /**
     * Enable sticky ATB scroll events
     */
    enableStickyATBEvents = () => {
      if (this.boundHandleStickyATC && this.boundHandleResize) {
        window.addEventListener('scroll', this.boundHandleStickyATC);
        window.addEventListener('resize', this.boundHandleResize);
        this.handleStickyATC();
      }
    }

    /**
     * Disable sticky ATB scroll events
     */
    disableStickyATBEvents = () => {
      if (this.boundHandleStickyATC && this.boundHandleResize) {
        window.removeEventListener('scroll', this.boundHandleStickyATC);
        window.removeEventListener('resize', this.boundHandleResize);
        this.resetStickyATBStyles();
      }
    }

    /**
     * Reset sticky ATB styles
     */
    resetStickyATBStyles = () => {
      const actionsElement = this.shadowRoot?.querySelector('.cyo-step__actions');
      if (actionsElement) {
        actionsElement.classList.remove('cyo-step__actions--sticky', 'cyo-step__actions--sticky-stop');
      }
    }

    /**
     * Handle sticky ATC positioning on scroll
     */
    handleStickyATC = () => {
      if (!this.enableStickyATB) return;

      const actionsElement = this.shadowRoot?.querySelector('.cyo-step__actions');
      if (!actionsElement) return;

      const mainContent = document.getElementById('main-content');
      if (!mainContent) return;

      const mainContentRect = mainContent.getBoundingClientRect();
      const scrollTop = window.scrollY;
      // Read from body, not documentElement: layout-height-observer.js writes the
      // measured pixel value there. On :root only the critical-css declaration is
      // visible, which is an unresolved calc() string -- parseInt gives NaN and
      // silently falls back to 92, so the threshold never tracked the real header.
      const totalHeaderHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10) || 92;

      // Get the actions element position relative to the viewport
      const actionsRect = actionsElement.getBoundingClientRect();
      const actionsTop = actionsRect.top + scrollTop;

      // First check if we should be sticky at all based on scroll position and enableStickyATBOn setting
      let shouldBeSticky = false;

      if (this.enableStickyATBOn === 'render') {
        shouldBeSticky = true; // Always sticky when set to render
      } else if (this.enableStickyATBOn === 'scroll') {
        shouldBeSticky = scrollTop > (actionsTop - totalHeaderHeight);
      }

      if (!shouldBeSticky) {
        actionsElement.classList.remove('cyo-step__actions--sticky', 'cyo-step__actions--sticky-stop');
        return;
      }

      // Then check main content visibility
      const mainContentBottom = mainContentRect.bottom;
      const viewportHeight = window.innerHeight;

      if (mainContentBottom <= viewportHeight) {
        actionsElement.classList.add('cyo-step__actions--sticky-stop');
        actionsElement.classList.remove('cyo-step__actions--sticky');
      } else {
        actionsElement.classList.add('cyo-step__actions--sticky');
        actionsElement.classList.remove('cyo-step__actions--sticky-stop');
      }
    }

    /**
     * Handle window resize for sticky positioning
     */
    handleResize = () => {
      this.handleStickyATC();
    }

    /**
     * Get aggregate media URL with metadata for featured media tracking
     * Uses product ID + SKU validation instead of direct SKU matching
     * @returns {Object|null} Object with mediaUrl and aggregateGallery, or null if not found
     */
    findAggregateMediaMatchForVariants = (selectedVariants) => {
      try {
        if (!Array.isArray(selectedVariants) || selectedVariants.length < 2) {
          return null;
        }

        const selectedSkus = new Set(
          selectedVariants.map((variant) => variant?.sku).filter(Boolean)
        );

        if (!selectedSkus.size || !this.config.aggregate_media_galleries) {
          return null;
        }

        for (const gallery of Object.values(this.config.aggregate_media_galleries)) {
          if (!gallery.media_json_url) {
            continue;
          }

          if (
            !gallery.represented_products_count ||
            gallery.represented_products_count !== selectedVariants.length
          ) {
            continue;
          }

          const hasAllSkus = Array.from(selectedSkus).every(
            (sku) => gallery.represented_skus && gallery.represented_skus.includes(sku)
          );

          if (hasAllSkus) {
            return gallery;
          }
        }

        return null;
      } catch (error) {
        console.error("❌ CYO: Error matching aggregate media gallery:", error);
        return null;
      }
    };

    getAggregateMediaUrlWithMetadata = () => {
      try {
        // Get all completed selections in order (including current step if it has a selection)
        const selectedVariants = [];
        const selectedProductIds = new Set();
        const selectedSkus = new Set();

        for (
          let stepIndex = 0;
          stepIndex <= this.currentStepIndex;
          stepIndex++
        ) {
          const step = this.config.steps[stepIndex];
          if (step) {
            const selection = this.getSelectionForStep(step);
            if (selection && selection.variant && selection.variant.sku) {
              selectedVariants.push(selection.variant);
              selectedProductIds.add(selection.product.id.toString());
              selectedSkus.add(selection.variant.sku);
            }
          }
        }

        if (selectedVariants.length < 2) {
          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Need at least 2 selections for aggregate media, found ${selectedVariants.length}`
            );
          }
          return null;
        }

        if (this.allowDebug()) {
          console.log(
            `🖼️ CYO: Looking for aggregate media with ${selectedProductIds.size} product IDs and ${selectedSkus.size} SKUs`
          );
          console.log(
            `🖼️ CYO: Selected product IDs: [${Array.from(selectedProductIds).join(", ")}]`
          );
          console.log(
            `🖼️ CYO: Selected SKUs: [${Array.from(selectedSkus).join(", ")}]`
          );
        }

        const matchedAggregateGallery = this.findAggregateMediaMatchForVariants(selectedVariants);
        if (matchedAggregateGallery) {
          if (this.allowDebug()) {
            console.log(
              `🖼️ CYO: Found matching aggregate media gallery: ${matchedAggregateGallery.id || matchedAggregateGallery.handle || "unknown"}`,
              matchedAggregateGallery
            );
          }

          return {
            mediaUrl: matchedAggregateGallery.media_json_url,
            aggregateGallery: matchedAggregateGallery,
          };
        }

        if (this.allowDebug()) {
          console.log(
            `🖼️ CYO: No aggregate media gallery found matching the selection criteria`
          );
          if (this.config.aggregate_media_galleries) {
            console.log(
              `🖼️ CYO: Searched through ${Object.keys(this.config.aggregate_media_galleries).length} galleries`
            );
          }
        }
        return null;
      } catch (error) {
        console.error(
          "❌ CYO: Error getting aggregate media URL with metadata:",
          error
        );
        return null;
      }
    };
  }

  customElements.define("cyo-product", CYOProduct);
}
